import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Save, Edit3, X, RotateCcw, Eye, ExternalLink, Globe } from 'lucide-react';
import BoardPublicSvgPreview from '../components/BoardPublicSvgPreview';
import PinBulkElectricalDefaultsPanel from '../components/PinBulkElectricalDefaultsPanel';
import PinoutCalloutEditor from '../components/PinoutCalloutEditor';
import RichTextEditor from '../components/RichTextEditor';
import RichContentEditor from '../components/RichContentEditor';
import BoardGalleryEditor from '../components/BoardGalleryEditor';
import AffiliateLinksEditor from '../components/AffiliateLinksEditor';
import TutorialUrlsEditor from '../components/TutorialUrlsEditor';
import CapabilityTagsField from '../components/CapabilityTagsField';
import { richContentBodyStyles } from '../components/richContentBodyStyles';
import { adminFetch } from '../services/adminApi';
import { appUrl } from '../utils/appPaths';
import { deriveBoardSeoDescription, deriveBoardSeoKeywords } from '../utils/seo';
import {
  getBoardGalleryImageCaption,
  normalizeBoardGalleryImages,
  resolveBoardGalleryImageSrc
} from '../utils/boardGallery';
import {
  normalizeAffiliateLinks,
  validateAffiliateLinks
} from '../utils/affiliateLinks';
import {
  normalizeTutorialUrls,
  validateTutorialUrls
} from '../utils/tutorialUrls';
import {
  buildPinoutPreviewSvg,
  getCalloutLayoutKey,
  getPinLayoutKey,
  getRotatedBox,
  normalizeRotation,
  rotateCallouts,
  validatePinCallouts
} from '../utils/pinCalloutLayout';
import {
  areSvgViewBoxesEqual,
  applySvgViewBoxToMarkup,
  normalizeSvgViewBox,
  parseSvgViewBoxFromMarkup,
  rotateSvgViewBox
} from '../utils/svgViewBox';
import {
  BOARD_VERIFICATION_STATUS_OPTIONS,
  PIN_ROLE_OPTIONS,
  PROTOCOL_OPTIONS,
  SUGGESTED_PIN_CAPABILITY_TAGS,
  getBoardVerificationLabel,
  normalizeBooleanFlag,
  normalizeBoardVerificationStatus,
  normalizeSupportedProtocols,
  normalizeStringArray,
  toggleSupportedProtocolValue,
  withBoardCompatibilityDefaults,
  withPinCompatibilityDefaults
} from '../utils/compatibilityOptions';
import {
  ALT_PIN_CALLOUT_COLOR_OPTIONS,
  applySuggestedAlternateNameDefaults,
  formatSuggestedAlternateNameCoordinate,
  getEditablePinAlternateNameEntries,
  getSuggestedAlternateNamePosition,
  serializePinAlternateNames
} from '../utils/pinAlternateNames';
import {
  initializePinFunctionAutofill,
  updatePinWithFunctionAutofill
} from '../utils/pinFunctionAutofill';
import { createBoardPreviewDraft } from '../utils/boardPreviewDraft';
import {
  applyBulkElectricalValuesToPins,
  countEligibleBulkElectricalPins,
  createEmptyBulkElectricalValues,
  hasCompleteBulkElectricalValues,
} from '../utils/pinElectricalDefaults';
import { createBoardPublicPinoutImageUpload } from '../utils/boardPublicPinoutPng';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';

const DEFAULT_SVG_DISPLAY_SCALE = 1;
const EDITOR_LAYOUT_MAX_WIDTH = '1560px';
const EMPTY_BULK_ELECTRICAL_VALUES = createEmptyBulkElectricalValues();

const EditBoardContainer = styled.div`
  padding: 40px 0;
  width: min(${EDITOR_LAYOUT_MAX_WIDTH}, calc(100% - (var(--site-gutter) * 2)));
  margin: 0 auto;
  box-sizing: border-box;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  background: none;
  border: none;
  font-weight: 500;
  margin-bottom: 32px;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    color: #3b82f6;
    background-color: #f1f5f9;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #64748b;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: ${(props) =>
    props.$calloutMode
      ? '1fr'
      : 'minmax(0, 1.45fr) minmax(360px, 0.95fr)'};
  gap: 30px;

  @media (max-width: 1220px) {
    grid-template-columns: 1fr;
  }
`;

const BoardPreview = styled.div`
  width: 100%;
  max-width: none;
  margin: 0;
  background: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  box-sizing: border-box;
`;

const PreviewTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const EditorWorkspace = styled.div`
  display: block;
`;

const ActionRail = styled.aside`
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 1200;

  @media (max-width: 768px) {
    top: calc(var(--header-height) + 8px);
    bottom: auto;
    left: 12px;
    transform: none;
  }
`;

const MainColumn = styled.div`
  min-width: 0;

  @media (min-width: 769px) {
    padding-bottom: 112px;
  }
`;

const PinEditor = styled.div`
  width: 100%;
  max-width: none;
  margin: 0;
  background: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  box-sizing: border-box;
  max-height: min(820px, 78vh);
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;

  @media (min-width: 1221px) {
    position: sticky;
    top: calc(var(--header-height, 72px) + 16px);
  }
`;

const BulkPinMetadataPanel = styled.div`
  width: 100%;
  margin: 28px 0 0;
  background: white;
  border-radius: 16px;
  padding: 28px 30px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  box-sizing: border-box;
`;

const BulkPinMetadataHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const BulkPinMetadataTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
`;

const BulkPinMetadataSubtitle = styled.p`
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 1.6;
  color: #64748b;
  max-width: 860px;
`;

const BulkPinMetadataTextArea = styled.textarea`
  width: 100%;
  min-height: 260px;
  padding: 16px;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  background: #0f172a;
  color: #e2e8f0;
  font-family: 'Consolas', 'SFMono-Regular', 'Liberation Mono', Menlo, monospace;
  font-size: 13px;
  line-height: 1.6;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.14);
  }
`;

const BulkPinMetadataActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 16px;
`;

const BulkPinMetadataExample = styled.pre`
  margin: 14px 0 0;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #dbe4ea;
  background: #f8fafc;
  color: #334155;
  font-size: 12px;
  line-height: 1.55;
  overflow-x: auto;
  white-space: pre-wrap;
`;

const EditorTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PinList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PinItem = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #3b82f6;
    background: #f1f5f9;
  }
`;

const PinHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const PinTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const PinNumber = styled.div`
  font-size: 14px;
  color: #64748b;
  background: #e2e8f0;
  padding: 4px 8px;
  border-radius: 4px;
`;

const PinDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  color: #374151;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  color: #374151;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  color: #374151;
  resize: vertical;
  min-height: 60px;
  max-height: 100px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-y: auto;
  word-wrap: break-word;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const AltNameCalloutColorSection = styled.div`
  display: grid;
  gap: 6px;
`;

const AltNameCalloutColorLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
`;

const AltNameCalloutColorRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const AltNameCalloutColorButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['$selected', '$swatch'].includes(prop),
})`
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 999px;
  border: 2px solid ${props => props.$selected ? '#0f172a' : '#ffffff'};
  background: ${props => props.$swatch};
  box-shadow: ${props => props.$selected
    ? '0 0 0 2px rgba(15, 23, 42, 0.14)'
    : '0 1px 3px rgba(15, 23, 42, 0.18)'};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.18);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.24);
  }
`;

const ActionButtons = styled.div`
  background: white;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  display: flex;
  flex-direction: row;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  width: max-content;
  max-width: calc(100vw - 48px);
  box-sizing: border-box;
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.14);
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    width: 56px;
    padding: 8px;
    gap: 8px;
    border-radius: 14px;
    flex-direction: column;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;
  width: auto;
  min-width: 152px;
  white-space: nowrap;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
  
  ${props => props.primary && `
    background: #3b82f6;
    color: white;
    
    &:hover {
      background: #2563eb;
    }
  `}
  
  ${props => props.secondary && `
    background: #f1f5f9;
    color: #64748b;
    
    &:hover {
      background: #e2e8f0;
    }
  `}

  ${props => props.danger && `
    background: #dc2626;
    color: white;

    &:hover {
      background: #b91c1c;
    }
  `}

  ${props => props.success && `
    background: #16a34a;
    color: white;

    &:hover {
      background: #15803d;
    }
  `}

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    min-width: 40px;
    padding: 0;
    justify-content: center;
    gap: 0;
    border-radius: 10px;
  }
`;

const ButtonText = styled.span`
  @media (max-width: 768px) {
    display: none;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  font-size: 18px;
  color: #64748b;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const NotificationStack = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1400;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: min(420px, calc(100vw - 32px));
  pointer-events: none;

  @media (max-width: 768px) {
    top: 12px;
    right: 12px;
    width: calc(100vw - 24px);
  }
`;

const FloatingNotification = styled.div`
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16);
  backdrop-filter: blur(10px);
  pointer-events: auto;
`;

const FloatingErrorMessage = styled(FloatingNotification)`
  background: rgba(254, 242, 242, 0.98);
  border: 1px solid #fecaca;
  color: #dc2626;
`;

const FloatingSuccessMessage = styled(FloatingNotification)`
  background: rgba(240, 253, 244, 0.98);
  border: 1px solid #bbf7d0;
  color: #16a34a;
`;

const BoardInfoSection = styled.div`
  width: 100%;
  max-width: none;
  margin: 0 0 24px;
  box-sizing: border-box;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
`;

const BoardInfoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const BoardInfoTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const EditBoardButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #2563eb;
  }
  
  &:disabled {
    background: #94a3b8;
    cursor: not-allowed;
  }
`;

const BoardInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BoardInfoField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BoardInfoFieldHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const BoardInfoInlineFields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const BoardInfoMiniField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BoardInfoHint = styled.div`
  font-size: 13px;
  line-height: 1.6;
  color: #64748b;
`;

const BoardInfoLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const BoardInfoInput = styled.input`
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &:disabled {
    background: #f9fafb;
    color: #6b7280;
  }
`;

const BoardInfoSelect = styled.select`
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &:disabled {
    background: #f9fafb;
    color: #6b7280;
  }
`;

const CodeEditorTextArea = styled.textarea`
  width: 100%;
  min-height: 320px;
  padding: 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.5;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  background: #0f172a;
  color: #e2e8f0;
  resize: vertical;
  box-sizing: border-box;
  tab-size: 2;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
`;

const CodeHelperText = styled.div`
  margin-top: 10px;
  font-size: 13px;
  line-height: 1.5;
  color: #64748b;
`;

const ValidationMessage = styled.div`
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.5;
  border: 1px solid ${props => props.$invalid ? '#fecaca' : '#bbf7d0'};
  background: ${props => props.$invalid ? '#fef2f2' : '#f0fdf4'};
  color: ${props => props.$invalid ? '#b91c1c' : '#15803d'};
`;

const BoardInfoDisplay = styled.div`
  padding: 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #374151;
  min-height: 20px;
  white-space: pre-wrap;
`;

const BoardRichContentDisplay = styled.div`
  ${richContentBodyStyles}
  padding: 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #374151;
  min-height: 20px;
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 10px;
`;

const CheckboxChip = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #dbe4f0;
  border-radius: 10px;
  background: #f8fafc;
  font-size: 13px;
  color: #334155;
  cursor: pointer;

  input {
    margin: 0;
  }
`;

const BoardAffiliateLinksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BoardAffiliateLinkItem = styled.a`
  display: block;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #dbeafe;
  background: #f8fbff;
  text-decoration: none;
`;

const BoardAffiliateLinkLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e40af;
  margin-bottom: 4px;
`;

const BoardAffiliateLinkUrl = styled.div`
  font-size: 13px;
  color: #64748b;
  word-break: break-all;
`;

const GalleryPreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;

  & > * {
    width: 100%;
    max-width: 400px;
    justify-self: center;
  }
`;

const GalleryPreviewLink = styled.a`
  display: block;
  width: 100%;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  text-decoration: none;
  color: inherit;
`;

const GalleryPreviewImage = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  display: block;
`;

const GalleryPreviewCaption = styled.div`
  padding: 10px 12px;
  font-size: 13px;
  color: #475569;
  background: white;
`;

const BoardInfoActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
`;

const BoardInfoButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f9fafb;
  }
  
  &.primary {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
    
    &:hover {
      background: #2563eb;
    }
    
    &:disabled {
      background: #94a3b8;
      border-color: #94a3b8;
      cursor: not-allowed;
    }
  }
  
  &.secondary {
    &:hover {
      background: #f3f4f6;
    }
  }
`;

const BoardInfoInlineButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  color: #1e40af;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #eff6ff;
    border-color: #93c5fd;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const BoardInfoFooterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
`;

const EditModeToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  background: ${props => props.active ? '#3b82f6' : 'white'};
  color: ${props => props.active ? 'white' : '#374151'};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? '#2563eb' : '#f9fafb'};
  }
`;

const PublicSvgControlsPanel = styled.div`
  margin-bottom: 20px;
  padding: 18px;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  background: linear-gradient(180deg, #f8fbff 0%, #eff6ff 100%);
`;

const PublicSvgControlsTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
`;

const PublicSvgControlsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 420px);
  justify-content: start;
  gap: 18px;

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const CalloutNotice = styled.div`
  margin-top: 18px;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid ${props => props.$tone === 'warning' ? '#fde68a' : '#bfdbfe'};
  background: ${props => props.$tone === 'warning' ? '#fffbeb' : '#eff6ff'};
  color: ${props => props.$tone === 'warning' ? '#92400e' : '#1d4ed8'};
  font-size: 14px;
  line-height: 1.6;
`;


const PinNameInput = styled.input`
  background: transparent;
  border: none;
  font-weight: 600;
  color: #1e293b;
  font-size: 16px;
  padding: 2px 4px;
  border-radius: 4px;
  min-width: 60px;
  
  &:focus {
    outline: none;
    background: white;
    border: 1px solid #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const PinNameDisplay = styled.div`
  font-weight: 600;
  color: #1e293b;
  font-size: 16px;
  padding: 2px 4px;
  border-radius: 4px;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const PinNameEditButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: #eff6ff;
    border-color: #93c5fd;
    color: #2563eb;
  }

  &:focus-visible {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const parseStoredPinCallouts = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value !== 'string') {
    return [];
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }

  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const PIN_METADATA_JSON_EXAMPLE = `[
  {
    "pin_number": "21",
    "group_name": "Communication",
    "pin_role": "i2c-sda",
    "capability_tags": ["gpio", "i2c-sda"],
    "use_with_caution": true,
    "caution_reason": "Shared with another onboard function."
  },
  {
    "pin_name": "GPIO22",
    "pin_role": "i2c-scl",
    "description": "<p>Primary I2C clock pin.</p>"
  }
]`;

const BOARD_METADATA_JSON_EXAMPLE = `{
  "manufacturer": "Arduino",
  "package_type": "DIP",
  "cpu": "ATmega328P",
  "supply_voltage_min_v": 1.8,
  "supply_voltage_max_v": 5.5,
  "logic_voltage_min_v": 3.3,
  "logic_voltage_max_v": 5,
  "current_draw_typ_ma": 45,
  "current_draw_max_ma": 200,
  "rail_3v3_max_ma": 50,
  "rail_5v_max_ma": 500,
  "clock_speed": "16 MHz",
  "flash": "32 KB",
  "ram": "2 KB",
  "rom": "",
  "supported_protocols": ["i2c", "spi", "uart"],
  "documentation_url": "https://docs.arduino.cc/",
  "datasheet_url": "https://example.com/datasheet.pdf",
  "verification_status": "unverified",
  "description": "<p>Classic ATmega328P development board.</p>"
}`;

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

const readImportField = (entry, keys) => {
  for (const key of keys) {
    if (hasOwn(entry, key)) {
      return entry[key];
    }
  }

  return undefined;
};

const hasImportField = (entry, keys) => keys.some((key) => hasOwn(entry, key));

const PIN_VOLTAGE_MIN_IMPORT_KEYS = ['voltage_min_v', 'voltageMinV', 'voltage_min', 'voltageMin', 'minimum_voltage', 'minimumVoltage', 'min_voltage', 'minVoltage'];
const PIN_VOLTAGE_MAX_IMPORT_KEYS = ['voltage_max_v', 'voltageMaxV', 'voltage_max', 'voltageMax', 'maximum_voltage', 'maximumVoltage', 'max_voltage', 'maxVoltage'];
const PIN_CURRENT_LIMIT_IMPORT_KEYS = ['current_limit_ma', 'currentLimitMa', 'current_limit', 'currentLimit', 'current_ma', 'currentMa'];

const normalizeImportNumber = (value, fieldLabel, { integer = false } = {}) => {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || value === '') {
    return '';
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    throw new Error(`${fieldLabel} must be numeric.`);
  }

  return integer ? Math.trunc(numericValue) : numericValue;
};

const normalizeImportString = (value) => {
  if (value === undefined) {
    return undefined;
  }

  if (value === null) {
    return '';
  }

  return String(value);
};

const normalizePinMetadataImportEntries = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === 'object' && Array.isArray(value.pins)) {
    return value.pins;
  }

  return null;
};

const normalizeBoardMetadataImportEntry = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if (value.board && typeof value.board === 'object' && !Array.isArray(value.board)) {
      return value.board;
    }

    if (value.metadata && typeof value.metadata === 'object' && !Array.isArray(value.metadata)) {
      return value.metadata;
    }

    if (value.board_metadata && typeof value.board_metadata === 'object' && !Array.isArray(value.board_metadata)) {
      return value.board_metadata;
    }

    return value;
  }

  return null;
};

const buildBoardMetadataExportPayload = (boardRecord) => ({
  manufacturer: String(boardRecord?.manufacturer ?? ''),
  package_type: String(boardRecord?.package_type ?? ''),
  cpu: String(boardRecord?.cpu ?? ''),
  supply_voltage_min_v: normalizeComparableNumber(boardRecord?.supply_voltage_min_v),
  supply_voltage_max_v: normalizeComparableNumber(boardRecord?.supply_voltage_max_v),
  logic_voltage_min_v: normalizeComparableNumber(boardRecord?.logic_voltage_min_v),
  logic_voltage_max_v: normalizeComparableNumber(boardRecord?.logic_voltage_max_v),
  current_draw_typ_ma: normalizeComparableNumber(boardRecord?.current_draw_typ_ma),
  current_draw_max_ma: normalizeComparableNumber(boardRecord?.current_draw_max_ma),
  rail_3v3_max_ma: normalizeComparableNumber(boardRecord?.rail_3v3_max_ma),
  rail_5v_max_ma: normalizeComparableNumber(boardRecord?.rail_5v_max_ma),
  clock_speed: String(boardRecord?.clock_speed ?? ''),
  flash: String(boardRecord?.flash_memory ?? ''),
  ram: String(boardRecord?.ram ?? ''),
  rom: String(boardRecord?.rom ?? ''),
  supported_protocols: normalizeSupportedProtocols(boardRecord?.supported_protocols),
  documentation_url: String(boardRecord?.link ?? ''),
  datasheet_url: String(boardRecord?.datasheet_url ?? ''),
  verification_status: normalizeBoardVerificationStatus(boardRecord?.verification_status),
  description: String(boardRecord?.description ?? ''),
});

const buildExportableAlternateNameEntries = (pin) => (
  getEditablePinAlternateNameEntries(pin)
    .filter((entry) => String(entry?.name ?? '').trim())
    .map((entry) => {
      const exportedEntry = {
        name: String(entry?.name ?? '').trim(),
      };
      const normalizedX = normalizeComparableNumber(entry?.x);
      const normalizedY = normalizeComparableNumber(entry?.y);
      const normalizedColor = String(entry?.color ?? '').trim();

      if (normalizedX !== '') {
        exportedEntry.x = normalizedX;
      }

      if (normalizedY !== '') {
        exportedEntry.y = normalizedY;
      }

      if (normalizedColor) {
        exportedEntry.color = normalizedColor;
      }

      return Object.keys(exportedEntry).length === 1 ? exportedEntry.name : exportedEntry;
    })
);

const formatFramingDraftValue = (value, fallback = '0') => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return fallback;
  }

  return String(Number(numericValue.toFixed(2)));
};

const normalizeComparableNumber = (value) => {
  if (value === '' || value === null || value === undefined) {
    return '';
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value);
  }

  return Number(numericValue.toFixed(6));
};

const normalizeComparableStringArray = (value) => (
  Array.isArray(value)
    ? value
        .map((item) => String(item || '').trim())
        .filter(Boolean)
        .sort()
    : []
);

const normalizeComparableViewBox = (value) => {
  const normalizedViewBox = normalizeSvgViewBox(value);

  if (!normalizedViewBox) {
    return null;
  }

  return {
    minX: normalizeComparableNumber(normalizedViewBox.minX),
    minY: normalizeComparableNumber(normalizedViewBox.minY),
    width: normalizeComparableNumber(normalizedViewBox.width),
    height: normalizeComparableNumber(normalizedViewBox.height),
  };
};

const createBoardDetailsSnapshot = ({
  board,
  calloutSourceSvg,
  workingSvgViewBox,
  boardSvgRotation,
  publicSvgRotation,
  pinCallouts,
}) => {
  if (!board) {
    return null;
  }

  return JSON.stringify({
    name: board.name || '',
    description: board.description || '',
    meta_description: board.meta_description || '',
    seo_keywords: board.seo_keywords || '',
    manufacturer: board.manufacturer || '',
    package_type: board.package_type || '',
    cpu: board.cpu || '',
    pin_count: normalizeComparableNumber(board.pin_count),
    supply_voltage_min_v: normalizeComparableNumber(board.supply_voltage_min_v),
    supply_voltage_max_v: normalizeComparableNumber(board.supply_voltage_max_v),
    logic_voltage_min_v: normalizeComparableNumber(board.logic_voltage_min_v),
    logic_voltage_max_v: normalizeComparableNumber(board.logic_voltage_max_v),
    current_draw_typ_ma: normalizeComparableNumber(board.current_draw_typ_ma),
    current_draw_max_ma: normalizeComparableNumber(board.current_draw_max_ma),
    rail_3v3_max_ma: normalizeComparableNumber(board.rail_3v3_max_ma),
    rail_5v_max_ma: normalizeComparableNumber(board.rail_5v_max_ma),
    supported_protocols: normalizeSupportedProtocols(board.supported_protocols),
    protocol_notes: board.protocol_notes || '',
    clock_speed: board.clock_speed || '',
    flash_memory: board.flash_memory || '',
    ram: board.ram || '',
      rom: board.rom || '',
      image_url: board.image_url || '',
      gallery_images: normalizeBoardGalleryImages(board.gallery_images),
      affiliate_links: normalizeAffiliateLinks(board.affiliate_links),
      link: board.link || '',
      datasheet_url: board.datasheet_url || '',
      tutorial_urls: normalizeTutorialUrls(board.tutorial_urls),
      verification_status: normalizeBoardVerificationStatus(board.verification_status),
      category: board.category || '',
    svg_source_content: calloutSourceSvg || '',
    svg_display_scale: normalizeComparableNumber(board.svg_display_scale ?? DEFAULT_SVG_DISPLAY_SCALE),
    svg_viewbox: normalizeComparableViewBox(workingSvgViewBox),
    svg_rotation_deg: normalizeComparableNumber(boardSvgRotation),
    public_svg_rotation_deg: normalizeComparableNumber(publicSvgRotation),
    pin_callouts: Array.isArray(pinCallouts) ? pinCallouts : [],
    show_mcu_specs: Boolean(Number(board.show_mcu_specs)),
  });
};

const createPinDetailsSnapshot = (pins, pinPositions) => {
  const normalizedPins = Array.isArray(pins) ? [...pins] : [];

  normalizedPins.sort((leftPin, rightPin) => String(leftPin.id).localeCompare(String(rightPin.id)));

  return JSON.stringify(
    normalizedPins.map((pin) => {
      const position = pinPositions?.[pin.id];

      return {
        id: pin.id,
        pin_name: pin.pin_name || '',
        pin_group_id: pin.pin_group_id ?? '',
        functions: pin.functions || '',
        alternate_pin_names: serializePinAlternateNames(getEditablePinAlternateNameEntries(pin)),
        alternate_functions: pin.alternate_functions || '',
        description: pin.description || '',
        pin_role: pin.pin_role || '',
        voltage_min_v: normalizeComparableNumber(pin.voltage_min_v),
        voltage_max_v: normalizeComparableNumber(pin.voltage_max_v),
        current_limit_ma: normalizeComparableNumber(pin.current_limit_ma),
        capability_tags: normalizeComparableStringArray(pin.capability_tags),
        is_5v_tolerant: Boolean(pin.is_5v_tolerant),
        use_with_caution: Boolean(pin.use_with_caution),
        caution_reason: pin.caution_reason || '',
        position_x: normalizeComparableNumber(position ? position.x : pin.position_x),
        position_y: normalizeComparableNumber(position ? position.y : pin.position_y),
        svg_id: pin.svg_id || '',
      };
    })
  );
};

const EditBoard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [board, setBoard] = useState(null);
  const [pins, setPins] = useState([]);
  const [pinGroups, setPinGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingBoard, setEditingBoard] = useState(false);
  const [editMode, setEditMode] = useState('pins'); // 'pins' = inspect/edit pins, 'callouts' = edit pin callouts
  const [editingPinName, setEditingPinName] = useState(null);
  const [pinPositions, setPinPositions] = useState({});
  const [bulkElectricalValues, setBulkElectricalValues] = useState(EMPTY_BULK_ELECTRICAL_VALUES);
  const [applyBulkElectricalValues, setApplyBulkElectricalValues] = useState(false);
  const [calloutEditorRotation, setCalloutEditorRotation] = useState(0);
  const [pinCallouts, setPinCallouts] = useState([]);
  const [svgValidationError, setSvgValidationError] = useState(null);
  const [publicSvgScaleDraft, setPublicSvgScaleDraft] = useState(String(DEFAULT_SVG_DISPLAY_SCALE));
  const [publicSvgOffsetDraft, setPublicSvgOffsetDraft] = useState({ x: '0', y: '0' });
  const [boardMetadataJson, setBoardMetadataJson] = useState('');
  const [boardMetadataImportSummary, setBoardMetadataImportSummary] = useState('');
  const [applyingBoardMetadataJson, setApplyingBoardMetadataJson] = useState(false);
  const [pinMetadataJson, setPinMetadataJson] = useState('');
  const [pinMetadataImportSummary, setPinMetadataImportSummary] = useState('');
  const [applyingPinMetadataJson, setApplyingPinMetadataJson] = useState(false);
  const [savedBoardSnapshot, setSavedBoardSnapshot] = useState(null);
  const [savedPinsSnapshot, setSavedPinsSnapshot] = useState(null);
  const svgContainerRef = useRef(null);
  const alternateNameDefaultsAppliedBoardRef = useRef('');

  const getRequestErrorMessage = (err, fallbackMessage) => (
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    fallbackMessage
  );

  const validateSvgMarkup = (svgMarkup) => {
    const trimmedSvg = (svgMarkup || '').trim();

    if (!trimmedSvg) {
      return null;
    }

    if (!/<svg[\s>]/i.test(trimmedSvg)) {
      return 'SVG source must contain a root <svg> element.';
    }

    if (/<script\b/i.test(trimmedSvg)) {
      return 'SVG source cannot include <script> tags.';
    }

    if (/\son[a-z]+\s*=/i.test(trimmedSvg)) {
      return 'SVG source cannot include inline event handler attributes.';
    }

    if (/javascript\s*:/i.test(trimmedSvg)) {
      return 'SVG source cannot include javascript: URLs.';
    }

    if (/<foreignObject\b/i.test(trimmedSvg)) {
      return 'SVG source cannot include <foreignObject> elements.';
    }

    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(trimmedSvg, 'image/svg+xml');
    const parserError = parsedDocument.querySelector('parsererror');

    if (parserError) {
      return 'SVG source is not valid XML. Check for missing closing tags or broken attributes.';
    }

    if (parsedDocument.documentElement?.nodeName.toLowerCase() !== 'svg') {
      return 'SVG source must use <svg> as the root element.';
    }

    return null;
  };

  const calloutSourceSvg = board?.svg_source_content || board?.svg_content || '';
  const boardSvgRotation = useMemo(
    () => normalizeRotation(board?.svg_rotation_deg ?? 0),
    [board?.svg_rotation_deg]
  );
  const publicSvgRotation = useMemo(
    () => normalizeRotation(board?.public_svg_rotation_deg ?? 0),
    [board?.public_svg_rotation_deg]
  );
  const derivedSvgViewBox = useMemo(
    () => parseSvgViewBoxFromMarkup(board?.svg_content || calloutSourceSvg),
    [board?.svg_content, calloutSourceSvg]
  );
  const workingSvgViewBox = board?.svg_viewbox || derivedSvgViewBox;
  const baseBoardViewBox = useMemo(() => {
    if (calloutSourceSvg) {
      const sourceViewBox = parseSvgViewBoxFromMarkup(calloutSourceSvg);
      return normalizeSvgViewBox(sourceViewBox ? getRotatedBox(sourceViewBox, boardSvgRotation) : null);
    }

    return derivedSvgViewBox;
  }, [boardSvgRotation, calloutSourceSvg, derivedSvgViewBox]);
  const editorWorkingSvgViewBox = useMemo(() => {
    if (!workingSvgViewBox) {
      return null;
    }

    if (!calloutSourceSvg || boardSvgRotation === calloutEditorRotation) {
      return workingSvgViewBox;
    }

    return rotateSvgViewBox(
      workingSvgViewBox,
      calloutSourceSvg,
      boardSvgRotation,
      calloutEditorRotation
    );
  }, [boardSvgRotation, calloutEditorRotation, calloutSourceSvg, workingSvgViewBox]);
  const publicSvgOffset = useMemo(() => {
    if (!workingSvgViewBox || !baseBoardViewBox) {
      return { x: 0, y: 0 };
    }

    const baseCenterX = baseBoardViewBox.minX + (baseBoardViewBox.width / 2);
    const baseCenterY = baseBoardViewBox.minY + (baseBoardViewBox.height / 2);
    const currentCenterX = workingSvgViewBox.minX + (workingSvgViewBox.width / 2);
    const currentCenterY = workingSvgViewBox.minY + (workingSvgViewBox.height / 2);

    return {
      x: Number((baseCenterX - currentCenterX).toFixed(2)),
      y: Number((baseCenterY - currentCenterY).toFixed(2)),
    };
  }, [baseBoardViewBox, workingSvgViewBox]);
  useEffect(() => {
    setPublicSvgScaleDraft(
      formatFramingDraftValue(board?.svg_display_scale ?? DEFAULT_SVG_DISPLAY_SCALE, String(DEFAULT_SVG_DISPLAY_SCALE))
    );
  }, [board?.svg_display_scale]);
  useEffect(() => {
    setPublicSvgOffsetDraft({
      x: formatFramingDraftValue(publicSvgOffset.x, '0'),
      y: formatFramingDraftValue(publicSvgOffset.y, '0'),
    });
  }, [publicSvgOffset.x, publicSvgOffset.y]);
  const calloutViewportReferenceSvg = useMemo(
    () => applySvgViewBoxToMarkup(calloutSourceSvg || board?.svg_content || '', workingSvgViewBox) || (calloutSourceSvg || board?.svg_content || ''),
    [board?.svg_content, calloutSourceSvg, workingSvgViewBox]
  );
  const pinCalloutValidation = useMemo(
    () => validatePinCallouts(pins || [], pinCallouts),
    [pins, pinCallouts]
  );
  const editorPinCallouts = useMemo(() => {
    if (!calloutSourceSvg || boardSvgRotation === calloutEditorRotation) {
      return pinCallouts;
    }

    return rotateCallouts(
      pinCallouts,
      calloutSourceSvg,
      boardSvgRotation,
      calloutEditorRotation
    );
  }, [boardSvgRotation, calloutEditorRotation, calloutSourceSvg, pinCallouts]);
  const finalPublicRotation = useMemo(
    () => normalizeRotation(boardSvgRotation + publicSvgRotation),
    [boardSvgRotation, publicSvgRotation]
  );
  const publicPreviewCallouts = useMemo(() => {
    if (!calloutSourceSvg || finalPublicRotation === boardSvgRotation) {
      return pinCalloutValidation.callouts;
    }

    return rotateCallouts(
      pinCalloutValidation.callouts,
      calloutSourceSvg,
      boardSvgRotation,
      finalPublicRotation
    );
  }, [boardSvgRotation, calloutSourceSvg, finalPublicRotation, pinCalloutValidation.callouts]);
  const publicPreviewViewBox = useMemo(() => {
    if (!workingSvgViewBox || !calloutSourceSvg || finalPublicRotation === boardSvgRotation) {
      return workingSvgViewBox;
    }

    return rotateSvgViewBox(
      workingSvgViewBox,
      calloutSourceSvg,
      boardSvgRotation,
      finalPublicRotation
    );
  }, [boardSvgRotation, calloutSourceSvg, finalPublicRotation, workingSvgViewBox]);
  const publicPreviewSvg = useMemo(() => {
    if (!calloutSourceSvg) {
      return applySvgViewBoxToMarkup(board?.svg_content || '', publicPreviewViewBox) || (board?.svg_content || '');
    }

    return buildPinoutPreviewSvg(calloutSourceSvg, pins || [], publicPreviewCallouts, finalPublicRotation, {
      viewBoundsMode: 'board',
      lockedViewBox: publicPreviewViewBox || null,
      normalizeCalloutSize: false,
    });
  }, [
    board?.svg_content,
    calloutSourceSvg,
    finalPublicRotation,
    pins,
      publicPreviewCallouts,
      publicPreviewViewBox,
    ]);
  const boardTutorialUrls = useMemo(
    () => normalizeTutorialUrls(board?.tutorial_urls),
    [board?.tutorial_urls]
  );
  const alternateNamePositionSuggestionsByPinId = useMemo(() => {
    const calloutsByKey = new Map(
      (Array.isArray(publicPreviewCallouts) ? publicPreviewCallouts : []).map((callout) => [
        getCalloutLayoutKey(callout),
        callout,
      ])
    );

    return Object.fromEntries(
      (Array.isArray(pins) ? pins : []).map((pin) => {
        const callout = calloutsByKey.get(getPinLayoutKey(pin));
        const editableEntries = getEditablePinAlternateNameEntries(pin);
        const suggestions = editableEntries.map((entry, index) => (
          getSuggestedAlternateNamePosition({
            pin,
            callout,
            index,
            alternateNameEntries: editableEntries,
          })
        ));

        return [String(pin?.id ?? ''), suggestions];
      })
    );
  }, [pins, publicPreviewCallouts]);
  const currentBoardSnapshot = useMemo(
    () => createBoardDetailsSnapshot({
      board,
      calloutSourceSvg,
      workingSvgViewBox,
      boardSvgRotation,
      publicSvgRotation,
      pinCallouts: pinCalloutValidation.callouts,
    }),
    [board, boardSvgRotation, calloutSourceSvg, pinCalloutValidation.callouts, publicSvgRotation, workingSvgViewBox]
  );
  const currentPinsSnapshot = useMemo(
    () => createPinDetailsSnapshot(pins, pinPositions),
    [pinPositions, pins]
  );
  const hasUnsavedBoardChanges = savedBoardSnapshot !== null && currentBoardSnapshot !== savedBoardSnapshot;
  const hasUnsavedPinChanges = savedPinsSnapshot !== null && currentPinsSnapshot !== savedPinsSnapshot;
  const hasUnsavedChanges = hasUnsavedBoardChanges || hasUnsavedPinChanges;
  const pinGroupNameById = useMemo(
    () => new Map(
      (Array.isArray(pinGroups) ? pinGroups : [])
        .map((group) => [String(group?.id ?? ''), String(group?.name ?? group?.group_name ?? '').trim()])
        .filter(([groupId, groupName]) => groupId && groupName)
    ),
    [pinGroups]
  );
  const bulkEligiblePinCount = useMemo(
    () => countEligibleBulkElectricalPins(pins),
    [pins]
  );

  useEffect(() => {
    const boardKey = String(id ?? '');
    const loadedBoardKey = String(board?.id ?? board?.board_id ?? board?.boardId ?? '');

    if (
      loading
      || !board
      || !boardKey
      || (loadedBoardKey && loadedBoardKey !== boardKey)
      || alternateNameDefaultsAppliedBoardRef.current === boardKey
    ) {
      return;
    }

    alternateNameDefaultsAppliedBoardRef.current = boardKey;

    setPins((currentPins) => {
      let hasChanges = false;
      const nextPins = (Array.isArray(currentPins) ? currentPins : []).map((pin) => {
        const currentEntries = getEditablePinAlternateNameEntries(pin);
        const currentSerialized = serializePinAlternateNames(currentEntries);
        const nextSerialized = serializePinAlternateNames(
          applySuggestedAlternateNameDefaults(
            currentEntries,
            alternateNamePositionSuggestionsByPinId[String(pin?.id ?? '')] || []
          )
        );

        if (nextSerialized === currentSerialized) {
          return pin;
        }

        hasChanges = true;
        return {
          ...pin,
          alternate_pin_names: nextSerialized,
        };
      });

      return hasChanges ? nextPins : currentPins;
    });
  }, [alternateNamePositionSuggestionsByPinId, board, id, loading]);

  useEffect(() => {
    alternateNameDefaultsAppliedBoardRef.current = '';
    setSavedBoardSnapshot(null);
    setSavedPinsSnapshot(null);
    setBulkElectricalValues(EMPTY_BULK_ELECTRICAL_VALUES);
    setApplyBulkElectricalValues(false);
    setBoardMetadataJson('');
    setBoardMetadataImportSummary('');
    setPinMetadataJson('');
    setPinMetadataImportSummary('');
  }, [id]);

  useEffect(() => {
    if (!loading && board && currentBoardSnapshot !== null && savedBoardSnapshot === null) {
      setSavedBoardSnapshot(currentBoardSnapshot);
    }
  }, [board, currentBoardSnapshot, loading, savedBoardSnapshot]);

  useEffect(() => {
    if (!loading && board && currentPinsSnapshot !== null && savedPinsSnapshot === null) {
      setSavedPinsSnapshot(currentPinsSnapshot);
    }
  }, [board, currentPinsSnapshot, loading, savedPinsSnapshot]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const { adminBoardsAPI, adminPinGroupsAPI, adminCategoriesAPI } = await import('../services/adminApi');

        const boardResponse = await adminBoardsAPI.getById(id);
        const rawBoardData = boardResponse.data;
        const sourceSvgContent = rawBoardData?.svg_source_content || rawBoardData?.svg_content || '';
        const resolvedSvgContent = rawBoardData?.svg_content || '';
        const normalizedBoardData = withBoardCompatibilityDefaults({
          ...rawBoardData,
          meta_description: deriveBoardSeoDescription(rawBoardData),
          seo_keywords: deriveBoardSeoKeywords(rawBoardData),
          show_mcu_specs: Number(rawBoardData?.show_mcu_specs ?? rawBoardData?.showMcuSpecs ?? 1) ? 1 : 0,
          svg_source_content: sourceSvgContent,
          svg_display_scale: rawBoardData?.svg_display_scale ?? rawBoardData?.svgDisplayScale ?? DEFAULT_SVG_DISPLAY_SCALE,
          svg_rotation_deg: normalizeRotation(rawBoardData?.svg_rotation_deg ?? rawBoardData?.svgRotationDeg ?? 0),
          public_svg_rotation_deg: normalizeRotation(
            rawBoardData?.public_svg_rotation_deg ?? rawBoardData?.publicSvgRotationDeg ?? 0
          ),
          pin_callouts: parseStoredPinCallouts(rawBoardData?.pin_callouts ?? rawBoardData?.pinCallouts),
          svg_viewbox: normalizeSvgViewBox(rawBoardData?.svg_viewbox ?? rawBoardData?.svgViewBox)
            || parseSvgViewBoxFromMarkup(resolvedSvgContent || sourceSvgContent)
        });
        
        console.log('Board data:', normalizedBoardData);
        console.log('Board SVG content:', normalizedBoardData?.svg_content ? 'Present' : 'Missing');
        
        setBoard(normalizedBoardData);
        setCalloutEditorRotation(normalizedBoardData.svg_rotation_deg);
        setPinCallouts(normalizedBoardData.pin_callouts);
        setSvgValidationError(validateSvgMarkup(sourceSvgContent));

        const [pinsResponse, groupsResponse, categoriesResponse] = await Promise.allSettled([
          adminBoardsAPI.getPins(id),
          adminPinGroupsAPI.getAll(),
          adminCategoriesAPI.getAll()
        ]);

        if (pinsResponse.status === 'fulfilled') {
          const pinsData = pinsResponse.value.data;
          console.log('Pins data:', pinsData);
          setPins(
            Array.isArray(pinsData)
              ? pinsData.map((pin) => ({
                  ...initializePinFunctionAutofill({
                    ...withPinCompatibilityDefaults(pin),
                    alternate_pin_names: pin?.alternate_pin_names ?? pin?.alternatePinNames ?? ''
                  })
                }))
              : []
          );
        } else {
          console.error('Error fetching pins:', pinsResponse.reason);
          setPins([]);
          setError(getRequestErrorMessage(pinsResponse.reason, 'Board loaded, but pin data could not be loaded.'));
        }

        if (groupsResponse.status === 'fulfilled') {
          const groupsData = groupsResponse.value.data;
          console.log('Groups data:', groupsData);
          setPinGroups(Array.isArray(groupsData) ? groupsData : []);
        } else {
          console.error('Error fetching pin groups:', groupsResponse.reason);
          setPinGroups([]);
          setError(prev => prev || getRequestErrorMessage(groupsResponse.reason, 'Board loaded, but pin groups could not be loaded.'));
        }

        if (categoriesResponse.status === 'fulfilled') {
          const categoriesData = categoriesResponse.value.data;
          console.log('Categories data:', categoriesData);
          setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        } else {
          console.error('Error fetching categories:', categoriesResponse.reason);
          setCategories([]);
          setError(prev => prev || getRequestErrorMessage(categoriesResponse.reason, 'Board loaded, but categories could not be loaded.'));
        }
      } catch (err) {
        setError(getRequestErrorMessage(err, 'Failed to load board data'));
        console.error('Error fetching data:', err);
        setBoard(null);
        setPins([]); // Ensure pins is always an array
        setPinGroups([]);
        setCategories([]);
        setCalloutEditorRotation(0);
        setPinCallouts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Initialize pin positions from database
  useEffect(() => {
    if (pins && pins.length > 0) {
      const initialPositions = {};
      pins.forEach(pin => {
        if (pin.position_x !== null && pin.position_x !== undefined && 
            pin.position_y !== null && pin.position_y !== undefined) {
          initialPositions[pin.id] = {
            x: pin.position_x,
            y: pin.position_y
          };
        }
      });
      setPinPositions(initialPositions);
    }
  }, [pins]);

  // Apply pin positions to SVG elements when they're available
  useEffect(() => {
    if (pins && pins.length > 0 && Object.keys(pinPositions).length > 0) {
      // Small delay to ensure SVG is rendered
      const timer = setTimeout(() => {
        pins.forEach(pin => {
          if (pin.svg_id && pinPositions[pin.id]) {
            const svgElement = document.getElementById(pin.svg_id);
            if (svgElement) {
              const pos = pinPositions[pin.id];
              // For board 518, don't apply negative coordinates on initial load
              // Only apply if coordinates are positive or if user has manually changed them
              const isBoard518 = parseInt(id) === 518;
              const hasUserChanges = pinPositions[pin.id] && (
                pinPositions[pin.id].x !== pin.position_x || 
                pinPositions[pin.id].y !== pin.position_y
              );
              
              if (!isBoard518 || pos.x >= 0 || pos.y >= 0 || hasUserChanges) {
                svgElement.setAttribute('x', pos.x.toString());
                svgElement.setAttribute('y', pos.y.toString());
              }
            }
          }
        });
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [pins, pinPositions, id]);

  const handlePinUpdate = (pinId, field, value) => {
    setPins(prevPins => 
      prevPins.map(pin => 
        pin.id === pinId 
          ? updatePinWithFunctionAutofill(pin, field, value)
          : pin
      )
    );
  };

  const applyBulkElectricalDefaults = (nextValues) => {
    if (!hasCompleteBulkElectricalValues(nextValues)) {
      return;
    }

    setPins((currentPins) => applyBulkElectricalValuesToPins(currentPins, nextValues));
  };

  const handleBulkElectricalValueChange = (field, value) => {
    const nextValues = {
      ...bulkElectricalValues,
      [field]: value,
    };

    setBulkElectricalValues(nextValues);

    if (applyBulkElectricalValues) {
      applyBulkElectricalDefaults(nextValues);
    }
  };

  const handleApplyBulkElectricalValuesChange = (checked) => {
    setApplyBulkElectricalValues(checked);

    if (checked) {
      applyBulkElectricalDefaults(bulkElectricalValues);
    }
  };

  const handlePinAlternateNameChange = (pinId, index, field, value) => {
    setPins((prevPins) =>
      prevPins.map((pin) => {
        if (pin.id !== pinId) {
          return pin;
        }

        const nextAlternateNames = getEditablePinAlternateNameEntries(pin);
        nextAlternateNames[index] = {
          ...nextAlternateNames[index],
          [field]: value,
        };
        const alternateNamesWithDefaults = field === 'name'
          ? applySuggestedAlternateNameDefaults(
              nextAlternateNames,
              alternateNamePositionSuggestionsByPinId[String(pinId)] || []
            )
          : nextAlternateNames;

        return {
          ...pin,
          alternate_pin_names: serializePinAlternateNames(alternateNamesWithDefaults)
        };
      })
    );
  };

  const focusPinConfig = (pinId) => {
    const pinElement = document.getElementById(`pin-${pinId}`);
    if (!pinElement) return;

    pinElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    pinElement.style.background = '#dbeafe';
    setTimeout(() => {
      pinElement.style.background = '';
    }, 2000);
  };


  const handlePinPositionChange = (pinId, field, value) => {
    const numValue = parseFloat(value) || 0;
    setPinPositions(prev => ({
      ...prev,
      [pinId]: {
        ...prev[pinId],
        [field]: numValue
      }
    }));

    // Update the SVG element position in real-time
    const pin = pins.find(p => p.id === pinId);
    if (pin && pin.svg_id) {
      const svgElement = document.getElementById(pin.svg_id);
      if (svgElement) {
        const currentPos = pinPositions[pinId] || { x: pin.position_x || 0, y: pin.position_y || 0 };
        const newPos = { ...currentPos, [field]: numValue };
        
        // Always update the position when user types in the fields
        svgElement.setAttribute('x', newPos.x.toString());
        svgElement.setAttribute('y', newPos.y.toString());
      }
    }
  };

  const handleBoardUpdate = (field, value) => {
    if (field === 'svg_source_content') {
      setSvgValidationError(validateSvgMarkup(value));
    }

    setBoard((prevBoard) => {
      if (field === 'svg_viewbox' && areSvgViewBoxesEqual(prevBoard?.svg_viewbox, value)) {
        return prevBoard;
      }

      return {
        ...prevBoard,
        [field]: value
      };
    });
  };

  const handleRegenerateMetaDescription = () => {
    setBoard((prevBoard) => {
      if (!prevBoard) {
        return prevBoard;
      }

      return {
        ...prevBoard,
        meta_description: deriveBoardSeoDescription({
          ...prevBoard,
          meta_description: '',
        }),
      };
    });
  };

  const handlePublicSvgOffsetChange = (axis, value) => {
    const nextOffsetValue = Number.parseFloat(value);
    const safeOffsetValue = Number.isFinite(nextOffsetValue) ? nextOffsetValue : 0;
    const referenceViewBox = normalizeSvgViewBox(workingSvgViewBox || baseBoardViewBox);

    if (!referenceViewBox || !baseBoardViewBox) {
      return;
    }

    const baseCenterX = baseBoardViewBox.minX + (baseBoardViewBox.width / 2);
    const baseCenterY = baseBoardViewBox.minY + (baseBoardViewBox.height / 2);
    const nextOffsetX = axis === 'x' ? safeOffsetValue : publicSvgOffset.x;
    const nextOffsetY = axis === 'y' ? safeOffsetValue : publicSvgOffset.y;

    handleBoardUpdate('svg_viewbox', normalizeSvgViewBox({
      minX: baseCenterX - nextOffsetX - (referenceViewBox.width / 2),
      minY: baseCenterY - nextOffsetY - (referenceViewBox.height / 2),
      width: referenceViewBox.width,
      height: referenceViewBox.height,
    }));
  };

  const handlePublicSvgScaleDraftChange = (value) => {
    setPublicSvgScaleDraft(value);

    const nextScale = Number.parseFloat(value);
    if (!Number.isFinite(nextScale)) {
      return;
    }

    handleBoardUpdate('svg_display_scale', nextScale);
  };

  const handlePublicSvgScaleDraftBlur = () => {
    setPublicSvgScaleDraft(
      formatFramingDraftValue(board?.svg_display_scale ?? DEFAULT_SVG_DISPLAY_SCALE, String(DEFAULT_SVG_DISPLAY_SCALE))
    );
  };

  const handlePublicSvgOffsetDraftChange = (axis, value) => {
    setPublicSvgOffsetDraft((currentDraft) => ({
      ...currentDraft,
      [axis]: value,
    }));

    const nextOffsetValue = Number.parseFloat(value);
    if (!Number.isFinite(nextOffsetValue)) {
      return;
    }

    handlePublicSvgOffsetChange(axis, value);
  };

  const handlePublicSvgOffsetDraftBlur = () => {
    setPublicSvgOffsetDraft({
      x: formatFramingDraftValue(publicSvgOffset.x, '0'),
      y: formatFramingDraftValue(publicSvgOffset.y, '0'),
    });
  };

  const handlePublicSvgRotationChange = (value) => {
    handleBoardUpdate('public_svg_rotation_deg', normalizeRotation(value));
  };

  const handleEditorCalloutsChange = (nextEditorCallouts) => {
    if (!calloutSourceSvg || boardSvgRotation === calloutEditorRotation) {
      setPinCallouts(nextEditorCallouts);
      return;
    }

    setPinCallouts(rotateCallouts(
      nextEditorCallouts,
      calloutSourceSvg,
      calloutEditorRotation,
      boardSvgRotation
    ));
  };

  const handleBoardProtocolToggle = (protocolValue) => {
    setBoard((prevBoard) => ({
      ...prevBoard,
      supported_protocols: toggleSupportedProtocolValue(prevBoard?.supported_protocols, protocolValue)
    }));
  };

  const createPinRequestPayload = (pin, position) => ({
    pin_name: pin.pin_name,
    pin_group_id: pin.pin_group_id === '' || pin.pin_group_id === undefined ? null : pin.pin_group_id,
    functions: pin.functions,
    voltage_range: pin.voltage_range ?? '',
    current_limit: pin.current_limit ?? '',
    alternate_pin_names: pin.alternate_pin_names,
    alternate_functions: pin.alternate_functions,
    description: pin.description,
    pin_role: pin.pin_role,
    voltage_min_v: pin.voltage_min_v,
    voltage_max_v: pin.voltage_max_v,
    current_limit_ma: pin.current_limit_ma,
    capability_tags: pin.capability_tags,
    is_5v_tolerant: pin.is_5v_tolerant,
    use_with_caution: pin.use_with_caution,
    caution_reason: pin.caution_reason,
    position_x: position && position.x !== '' ? position.x : (pin.position_x === '' ? null : pin.position_x),
    position_y: position && position.y !== '' ? position.y : (pin.position_y === '' ? null : pin.position_y),
    svg_id: pin.svg_id ?? ''
  });

  const copyTextToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    textArea.style.pointerEvents = 'none';
    document.body.appendChild(textArea);
    textArea.select();

    try {
      const didCopy = document.execCommand('copy');
      if (!didCopy) {
        throw new Error('Legacy clipboard copy failed.');
      }
    } finally {
      textArea.remove();
    }
  };

  const buildPinMetadataExportPayload = () => (
    (Array.isArray(pins) ? pins : []).map((pin) => {
      const position = pinPositions?.[pin.id];
      const groupName = pinGroupNameById.get(String(pin?.pin_group_id ?? ''))
        || String(pin?.group_name ?? pin?.groupName ?? '').trim();

      return {
        id: pin.id,
        pin_number: String(pin?.pin_number ?? ''),
        pin_name: String(pin?.pin_name ?? ''),
        group_name: groupName,
        pin_role: String(pin?.pin_role ?? ''),
        functions: String(pin?.functions ?? ''),
        capability_tags: normalizeStringArray(pin?.capability_tags),
        is_5v_tolerant: Boolean(pin?.is_5v_tolerant),
        use_with_caution: Boolean(pin?.use_with_caution),
        caution_reason: String(pin?.caution_reason ?? ''),
        alternate_pin_names: buildExportableAlternateNameEntries(pin),
        alternate_functions: String(pin?.alternate_functions ?? ''),
        description: String(pin?.description ?? ''),
        voltage_min_v: normalizeComparableNumber(pin?.voltage_min_v),
        voltage_max_v: normalizeComparableNumber(pin?.voltage_max_v),
        current_limit_ma: normalizeComparableNumber(pin?.current_limit_ma),
        position_x: normalizeComparableNumber(position ? position.x : pin?.position_x),
        position_y: normalizeComparableNumber(position ? position.y : pin?.position_y),
        svg_id: String(pin?.svg_id ?? pin?.svgId ?? ''),
      };
    })
  );

  const loadCurrentBoardMetadataJson = () => {
    setBoardMetadataJson(JSON.stringify(buildBoardMetadataExportPayload(board), null, 2));
    setBoardMetadataImportSummary('Loaded current board details as JSON.');
  };

  const handleCopyCurrentBoardMetadataJson = async () => {
    try {
      await copyTextToClipboard(JSON.stringify(buildBoardMetadataExportPayload(board), null, 2));
      setBoardMetadataImportSummary('Copied current board details JSON to the clipboard.');
    } catch (copyError) {
      setError(copyError?.message || 'Failed to copy current board details JSON.');
    }
  };

  const loadCurrentPinMetadataJson = () => {
    setPinMetadataJson(JSON.stringify(buildPinMetadataExportPayload(), null, 2));
    setPinMetadataImportSummary('Loaded current pin metadata as JSON.');
  };

  const handleCopyCurrentPinMetadataJson = async () => {
    try {
      await copyTextToClipboard(JSON.stringify(buildPinMetadataExportPayload(), null, 2));
      setPinMetadataImportSummary('Copied current pin metadata JSON to the clipboard.');
    } catch (copyError) {
      setError(copyError?.message || 'Failed to copy current pin metadata JSON.');
    }
  };

  const handleApplyBoardMetadataJson = async () => {
    if (saving || applyingBoardMetadataJson || !board) {
      return;
    }

    setBoardMetadataImportSummary('');

    const trimmedJson = String(boardMetadataJson || '').trim();
    if (!trimmedJson) {
      setError('Paste a JSON object of board metadata updates before applying.');
      return;
    }

    let parsedInput;

    try {
      parsedInput = JSON.parse(trimmedJson);
    } catch (parseError) {
      setError(`Board details JSON could not be parsed: ${parseError.message}`);
      return;
    }

    const importEntry = normalizeBoardMetadataImportEntry(parsedInput);
    if (!importEntry) {
      setError('Board details JSON must be an object, or an object with a nested "board" object.');
      return;
    }

    let nextBoard;

    try {
      nextBoard = {
        ...board,
      };

      if (hasImportField(importEntry, ['manufacturer'])) {
        nextBoard.manufacturer = normalizeImportString(readImportField(importEntry, ['manufacturer'])) || '';
      }

      if (hasImportField(importEntry, ['package_type', 'packageType', 'package', 'form_factor', 'formFactor'])) {
        nextBoard.package_type = normalizeImportString(readImportField(importEntry, ['package_type', 'packageType', 'package', 'form_factor', 'formFactor'])) || '';
      }

      if (hasImportField(importEntry, ['cpu'])) {
        nextBoard.cpu = normalizeImportString(readImportField(importEntry, ['cpu'])) || '';
      }

      if (hasImportField(importEntry, ['supply_voltage_min_v', 'supplyVoltageMinV'])) {
        nextBoard.supply_voltage_min_v = normalizeImportNumber(
          readImportField(importEntry, ['supply_voltage_min_v', 'supplyVoltageMinV']),
          'supply_voltage_min_v'
        );
      }

      if (hasImportField(importEntry, ['supply_voltage_max_v', 'supplyVoltageMaxV'])) {
        nextBoard.supply_voltage_max_v = normalizeImportNumber(
          readImportField(importEntry, ['supply_voltage_max_v', 'supplyVoltageMaxV']),
          'supply_voltage_max_v'
        );
      }

      if (hasImportField(importEntry, ['logic_voltage_min_v', 'logicVoltageMinV'])) {
        nextBoard.logic_voltage_min_v = normalizeImportNumber(
          readImportField(importEntry, ['logic_voltage_min_v', 'logicVoltageMinV']),
          'logic_voltage_min_v'
        );
      }

      if (hasImportField(importEntry, ['logic_voltage_max_v', 'logicVoltageMaxV'])) {
        nextBoard.logic_voltage_max_v = normalizeImportNumber(
          readImportField(importEntry, ['logic_voltage_max_v', 'logicVoltageMaxV']),
          'logic_voltage_max_v'
        );
      }

      if (hasImportField(importEntry, ['current_draw_typ_ma', 'currentDrawTypMa', 'typical_current_draw_ma', 'typicalCurrentDrawMa'])) {
        nextBoard.current_draw_typ_ma = normalizeImportNumber(
          readImportField(importEntry, ['current_draw_typ_ma', 'currentDrawTypMa', 'typical_current_draw_ma', 'typicalCurrentDrawMa']),
          'current_draw_typ_ma'
        );
      }

      if (hasImportField(importEntry, ['current_draw_max_ma', 'currentDrawMaxMa', 'max_current_draw_ma', 'maxCurrentDrawMa'])) {
        nextBoard.current_draw_max_ma = normalizeImportNumber(
          readImportField(importEntry, ['current_draw_max_ma', 'currentDrawMaxMa', 'max_current_draw_ma', 'maxCurrentDrawMa']),
          'current_draw_max_ma'
        );
      }

      if (hasImportField(importEntry, ['rail_3v3_max_ma', 'rail3v3MaxMa', 'rail_3v3_limit_ma', 'rail3v3LimitMa'])) {
        nextBoard.rail_3v3_max_ma = normalizeImportNumber(
          readImportField(importEntry, ['rail_3v3_max_ma', 'rail3v3MaxMa', 'rail_3v3_limit_ma', 'rail3v3LimitMa']),
          'rail_3v3_max_ma'
        );
      }

      if (hasImportField(importEntry, ['rail_5v_max_ma', 'rail5vMaxMa', 'rail_5v_limit_ma', 'rail5vLimitMa'])) {
        nextBoard.rail_5v_max_ma = normalizeImportNumber(
          readImportField(importEntry, ['rail_5v_max_ma', 'rail5vMaxMa', 'rail_5v_limit_ma', 'rail5vLimitMa']),
          'rail_5v_max_ma'
        );
      }

      if (hasImportField(importEntry, ['clock_speed', 'clockSpeed'])) {
        nextBoard.clock_speed = normalizeImportString(readImportField(importEntry, ['clock_speed', 'clockSpeed'])) || '';
      }

      if (hasImportField(importEntry, ['flash_memory', 'flashMemory', 'flash'])) {
        nextBoard.flash_memory = normalizeImportString(readImportField(importEntry, ['flash_memory', 'flashMemory', 'flash'])) || '';
      }

      if (hasImportField(importEntry, ['ram'])) {
        nextBoard.ram = normalizeImportString(readImportField(importEntry, ['ram'])) || '';
      }

      if (hasImportField(importEntry, ['rom'])) {
        nextBoard.rom = normalizeImportString(readImportField(importEntry, ['rom'])) || '';
      }

      if (hasImportField(importEntry, ['supported_protocols', 'supportedProtocols'])) {
        nextBoard.supported_protocols = normalizeSupportedProtocols(
          readImportField(importEntry, ['supported_protocols', 'supportedProtocols'])
        );
      }

      if (hasImportField(importEntry, ['documentation_url', 'documentationUrl', 'link', 'url'])) {
        nextBoard.link = normalizeImportString(
          readImportField(importEntry, ['documentation_url', 'documentationUrl', 'link', 'url'])
        ) || '';
      }

      if (hasImportField(importEntry, ['datasheet_url', 'datasheetUrl'])) {
        nextBoard.datasheet_url = normalizeImportString(
          readImportField(importEntry, ['datasheet_url', 'datasheetUrl'])
        ) || '';
      }

      if (hasImportField(importEntry, ['verification_status', 'verificationStatus'])) {
        nextBoard.verification_status = normalizeBoardVerificationStatus(
          readImportField(importEntry, ['verification_status', 'verificationStatus'])
        );
      }

      if (hasImportField(importEntry, ['description'])) {
        nextBoard.description = normalizeImportString(readImportField(importEntry, ['description'])) || '';
      }

      nextBoard = withBoardCompatibilityDefaults(nextBoard);
    } catch (importError) {
      setError(importError.message || 'Board details JSON could not be applied.');
      return;
    }

    setApplyingBoardMetadataJson(true);
    setError(null);
    setSuccess(null);

    try {
      const { adminBoardsAPI } = await import('../services/adminApi');
      const updateResponse = await adminBoardsAPI.update(
        id,
        buildBoardPayload(nextBoard, {
          svg_content: undefined,
          svg_source_content: undefined,
        })
      );
      const savedBoard = withBoardCompatibilityDefaults({
        ...nextBoard,
        ...updateResponse?.data?.data,
        gallery_images: Array.isArray(updateResponse?.data?.data?.gallery_images)
          ? updateResponse.data.data.gallery_images
          : nextBoard.gallery_images,
        affiliate_links: Array.isArray(updateResponse?.data?.data?.affiliate_links)
          ? updateResponse.data.data.affiliate_links
          : nextBoard.affiliate_links,
        tutorial_urls: Array.isArray(updateResponse?.data?.data?.tutorial_urls)
          ? updateResponse.data.data.tutorial_urls
          : nextBoard.tutorial_urls,
        pin_callouts: pinCalloutValidation.callouts,
        svg_source_content: updateResponse?.data?.data?.svg_source_content ?? nextBoard.svg_source_content ?? calloutSourceSvg,
        svg_display_scale: updateResponse?.data?.data?.svg_display_scale ?? nextBoard.svg_display_scale,
        svg_rotation_deg: updateResponse?.data?.data?.svg_rotation_deg ?? nextBoard.svg_rotation_deg,
        public_svg_rotation_deg: updateResponse?.data?.data?.public_svg_rotation_deg ?? nextBoard.public_svg_rotation_deg,
        svg_viewbox: workingSvgViewBox,
        svg_content: updateResponse?.data?.data?.svg_content ?? nextBoard.svg_content,
      });

      setBoard(savedBoard);
      setSavedBoardSnapshot(createBoardDetailsSnapshot({
        board: savedBoard,
        calloutSourceSvg,
        workingSvgViewBox,
        boardSvgRotation,
        publicSvgRotation,
        pinCallouts: pinCalloutValidation.callouts,
      }));
      setBoardMetadataImportSummary('Applied JSON metadata to the board details.');
      setSuccess('Board details JSON applied successfully.');
    } catch (importError) {
      setError(
        importError?.response?.data?.error
        || importError?.message
        || 'Failed to apply board details JSON.'
      );
    } finally {
      setApplyingBoardMetadataJson(false);
    }
  };

  const handleApplyPinMetadataJson = async () => {
    if (saving || applyingPinMetadataJson) {
      return;
    }

    setPinMetadataImportSummary('');

    const trimmedJson = String(pinMetadataJson || '').trim();
    if (!trimmedJson) {
      setError('Paste a JSON array of pin metadata updates before applying.');
      return;
    }

    let parsedInput;

    try {
      parsedInput = JSON.parse(trimmedJson);
    } catch (parseError) {
      setError(`Pin metadata JSON could not be parsed: ${parseError.message}`);
      return;
    }

    const importEntries = normalizePinMetadataImportEntries(parsedInput);
    if (!Array.isArray(importEntries) || importEntries.length === 0) {
      setError('Pin metadata JSON must be an array, or an object with a non-empty "pins" array.');
      return;
    }

    const pinGroupsByName = new Map(
      (Array.isArray(pinGroups) ? pinGroups : []).map((group) => [
        String(group?.name || '').trim().toLowerCase(),
        group,
      ])
    );
    const nextPinsById = new Map((Array.isArray(pins) ? pins : []).map((pin) => [String(pin.id), pin]));
    const nextPinPositions = { ...pinPositions };
    const touchedPinIds = new Set();

    const resolvePinFromEntry = (entry, entryIndex) => {
      const idValue = readImportField(entry, ['id']);
      if (idValue !== undefined && idValue !== null && idValue !== '') {
        const matchedPin = (pins || []).find((candidate) => String(candidate.id) === String(idValue));
        if (!matchedPin) {
          throw new Error(`Entry ${entryIndex + 1} references pin id "${idValue}", but no pin matches it.`);
        }

        return matchedPin;
      }

      const pinNumberValue = readImportField(entry, ['pin_number', 'pinNumber']);
      if (pinNumberValue !== undefined && pinNumberValue !== null && pinNumberValue !== '') {
        const matchedPin = (pins || []).find((candidate) => String(candidate.pin_number) === String(pinNumberValue));
        if (!matchedPin) {
          throw new Error(`Entry ${entryIndex + 1} references pin_number "${pinNumberValue}", but no pin matches it.`);
        }

        return matchedPin;
      }

      const pinNameValue = normalizeImportString(readImportField(entry, ['pin_name', 'pinName']));
      if (pinNameValue !== undefined && pinNameValue.trim() !== '') {
        const normalizedPinName = pinNameValue.trim().toLowerCase();
        const matchedPins = (pins || []).filter((candidate) => String(candidate.pin_name || '').trim().toLowerCase() === normalizedPinName);

        if (matchedPins.length === 0) {
          throw new Error(`Entry ${entryIndex + 1} references pin_name "${pinNameValue}", but no pin matches it.`);
        }

        if (matchedPins.length > 1) {
          throw new Error(`Entry ${entryIndex + 1} references pin_name "${pinNameValue}", but that matches multiple pins. Use id or pin_number instead.`);
        }

        return matchedPins[0];
      }

      const svgIdValue = normalizeImportString(readImportField(entry, ['svg_id', 'svgId']));
      if (svgIdValue !== undefined && svgIdValue.trim() !== '') {
        const matchedPin = (pins || []).find((candidate) => String(candidate.svg_id || '').trim() === svgIdValue.trim());
        if (!matchedPin) {
          throw new Error(`Entry ${entryIndex + 1} references svg_id "${svgIdValue}", but no pin matches it.`);
        }

        return matchedPin;
      }

      throw new Error(`Entry ${entryIndex + 1} must identify a pin by id, pin_number, pin_name, or svg_id.`);
    };

    const resolvePinGroupId = (entry, entryIndex, currentPin) => {
      if (hasImportField(entry, ['pin_group_id', 'pinGroupId'])) {
        const rawPinGroupId = readImportField(entry, ['pin_group_id', 'pinGroupId']);
        if (rawPinGroupId === null || rawPinGroupId === '') {
          return null;
        }

        const normalizedPinGroupId = normalizeImportNumber(rawPinGroupId, `Pin group id for entry ${entryIndex + 1}`, { integer: true });
        const matchedGroup = (pinGroups || []).find((group) => Number(group.id) === Number(normalizedPinGroupId));

        if (!matchedGroup) {
          throw new Error(`Entry ${entryIndex + 1} references pin_group_id "${rawPinGroupId}", but that group does not exist.`);
        }

        return matchedGroup.id;
      }

      if (hasImportField(entry, ['group_name', 'pin_group_name', 'pinGroupName'])) {
        const rawGroupName = normalizeImportString(readImportField(entry, ['group_name', 'pin_group_name', 'pinGroupName'])) || '';
        const normalizedGroupName = rawGroupName.trim().toLowerCase();

        if (!normalizedGroupName) {
          return null;
        }

        const matchedGroup = pinGroupsByName.get(normalizedGroupName);
        if (!matchedGroup) {
          throw new Error(`Entry ${entryIndex + 1} references group_name "${rawGroupName}", but that group does not exist.`);
        }

        return matchedGroup.id;
      }

      return currentPin.pin_group_id ?? null;
    };

    const nextPins = [];

    try {
      importEntries.forEach((entry, entryIndex) => {
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
          throw new Error(`Entry ${entryIndex + 1} must be an object.`);
        }

        const currentPin = resolvePinFromEntry(entry, entryIndex);
        const currentPosition = nextPinPositions[currentPin.id] || {
          x: currentPin.position_x ?? '',
          y: currentPin.position_y ?? '',
        };

        if (touchedPinIds.has(String(currentPin.id))) {
          throw new Error(`Pin "${currentPin.pin_name || currentPin.pin_number || currentPin.id}" appears more than once in the import JSON.`);
        }

        touchedPinIds.add(String(currentPin.id));

        let nextPin = { ...currentPin };

        if (hasImportField(entry, ['pin_name', 'pinName'])) {
          nextPin = updatePinWithFunctionAutofill(
            nextPin,
            'pin_name',
            normalizeImportString(readImportField(entry, ['pin_name', 'pinName'])) || ''
          );
        }

        if (hasImportField(entry, ['functions', 'primary_function', 'primaryFunction'])) {
          nextPin = updatePinWithFunctionAutofill(
            nextPin,
            'functions',
            normalizeImportString(readImportField(entry, ['functions', 'primary_function', 'primaryFunction'])) || ''
          );
        }

        if (hasImportField(entry, ['pin_role', 'pinRole'])) {
          nextPin = updatePinWithFunctionAutofill(
            nextPin,
            'pin_role',
            normalizeImportString(readImportField(entry, ['pin_role', 'pinRole'])) || ''
          );
        }

        if (hasImportField(entry, ['capability_tags', 'capabilityTags'])) {
          nextPin = updatePinWithFunctionAutofill(
            nextPin,
            'capability_tags',
            normalizeStringArray(readImportField(entry, ['capability_tags', 'capabilityTags']))
          );
        }

        if (hasImportField(entry, ['alternate_functions', 'alternateFunctions', 'alternate_function', 'alternateFunction'])) {
          nextPin = updatePinWithFunctionAutofill(
            nextPin,
            'alternate_functions',
            normalizeImportString(readImportField(entry, ['alternate_functions', 'alternateFunctions', 'alternate_function', 'alternateFunction'])) || ''
          );
        }

        const nextUseWithCaution = hasImportField(entry, ['use_with_caution', 'useWithCaution'])
          ? normalizeBooleanFlag(readImportField(entry, ['use_with_caution', 'useWithCaution']))
          : Boolean(nextPin.use_with_caution);
        const importedAlternateNameEntries = hasImportField(entry, ['alternate_pin_names', 'alternatePinNames'])
          ? applySuggestedAlternateNameDefaults(
              readImportField(entry, ['alternate_pin_names', 'alternatePinNames']),
              alternateNamePositionSuggestionsByPinId[String(currentPin.id)] || []
            )
          : null;

        const nextPositionX = hasImportField(entry, ['position_x', 'positionX'])
          ? normalizeImportNumber(readImportField(entry, ['position_x', 'positionX']), `position_x for entry ${entryIndex + 1}`)
          : currentPosition.x;
        const nextPositionY = hasImportField(entry, ['position_y', 'positionY'])
          ? normalizeImportNumber(readImportField(entry, ['position_y', 'positionY']), `position_y for entry ${entryIndex + 1}`)
          : currentPosition.y;

        nextPin = {
          ...nextPin,
          pin_group_id: resolvePinGroupId(entry, entryIndex, nextPin),
          alternate_pin_names: hasImportField(entry, ['alternate_pin_names', 'alternatePinNames'])
            ? serializePinAlternateNames(importedAlternateNameEntries)
            : nextPin.alternate_pin_names,
          alternate_functions: nextPin.alternate_functions,
          description: hasImportField(entry, ['description'])
            ? normalizeImportString(readImportField(entry, ['description'])) || ''
            : nextPin.description,
          pin_role: nextPin.pin_role,
          current_limit: hasImportField(entry, ['current_limit', 'currentLimit'])
            ? normalizeImportNumber(readImportField(entry, ['current_limit', 'currentLimit']), `current_limit for entry ${entryIndex + 1}`)
            : nextPin.current_limit,
          voltage_min_v: hasImportField(entry, PIN_VOLTAGE_MIN_IMPORT_KEYS)
            ? normalizeImportNumber(readImportField(entry, PIN_VOLTAGE_MIN_IMPORT_KEYS), `voltage_min_v for entry ${entryIndex + 1}`)
            : nextPin.voltage_min_v,
          voltage_max_v: hasImportField(entry, PIN_VOLTAGE_MAX_IMPORT_KEYS)
            ? normalizeImportNumber(readImportField(entry, PIN_VOLTAGE_MAX_IMPORT_KEYS), `voltage_max_v for entry ${entryIndex + 1}`)
            : nextPin.voltage_max_v,
          current_limit_ma: hasImportField(entry, PIN_CURRENT_LIMIT_IMPORT_KEYS)
            ? normalizeImportNumber(readImportField(entry, PIN_CURRENT_LIMIT_IMPORT_KEYS), `current_limit_ma for entry ${entryIndex + 1}`)
            : nextPin.current_limit_ma,
          capability_tags: nextPin.capability_tags,
          is_5v_tolerant: hasImportField(entry, ['is_5v_tolerant', 'is5vTolerant'])
            ? normalizeBooleanFlag(readImportField(entry, ['is_5v_tolerant', 'is5vTolerant']))
            : Boolean(nextPin.is_5v_tolerant),
          use_with_caution: nextUseWithCaution,
          caution_reason: nextUseWithCaution
            ? (
                hasImportField(entry, ['caution_reason', 'cautionReason'])
                  ? normalizeImportString(readImportField(entry, ['caution_reason', 'cautionReason'])) || ''
                  : nextPin.caution_reason
              )
            : '',
          svg_id: hasImportField(entry, ['svg_id', 'svgId'])
            ? normalizeImportString(readImportField(entry, ['svg_id', 'svgId'])) || ''
            : (nextPin.svg_id ?? ''),
          position_x: nextPositionX,
          position_y: nextPositionY,
        };

        nextPinPositions[currentPin.id] = {
          x: nextPositionX === '' ? '' : nextPositionX,
          y: nextPositionY === '' ? '' : nextPositionY,
        };

        nextPinsById.set(String(currentPin.id), nextPin);
      });

      (pins || []).forEach((pin) => {
        nextPins.push(nextPinsById.get(String(pin.id)) || pin);
      });
    } catch (importError) {
      setError(importError.message || 'Pin metadata JSON could not be applied.');
      return;
    }

    setApplyingPinMetadataJson(true);
    setError(null);
    setSuccess(null);

    try {
      const { adminPinsAPI } = await import('../services/adminApi');
      const payloadPins = nextPins
        .filter((pin) => touchedPinIds.has(String(pin.id)))
        .map((pin) => ({
          id: pin.id,
          ...createPinRequestPayload(pin, nextPinPositions[pin.id]),
        }));

      await adminPinsAPI.bulkUpdate(payloadPins);

      setPins(nextPins);
      setPinPositions(nextPinPositions);
      setSavedPinsSnapshot(createPinDetailsSnapshot(nextPins, nextPinPositions));
      setPinMetadataImportSummary(`Applied JSON metadata to ${payloadPins.length} pin${payloadPins.length === 1 ? '' : 's'}.`);
      setSuccess(`Pin metadata JSON applied to ${payloadPins.length} pin${payloadPins.length === 1 ? '' : 's'}.`);
    } catch (importError) {
      setError(
        importError?.response?.data?.error
        || importError?.message
        || 'Failed to apply pin metadata JSON.'
      );
    } finally {
      setApplyingPinMetadataJson(false);
    }
  };

  const handleCalloutRotationChange = (delta) => {
    setCalloutEditorRotation((currentRotation) => normalizeRotation(currentRotation + delta));
  };

  const buildBoardPayload = (boardSource = board, overrides = {}) => ({
    name: boardSource?.name,
    description: boardSource?.description,
    meta_description: boardSource?.meta_description || '',
    seo_keywords: boardSource?.seo_keywords || '',
    manufacturer: boardSource?.manufacturer,
    package_type: boardSource?.package_type,
    cpu: boardSource?.cpu,
    pin_count: boardSource?.pin_count,
    supply_voltage_min_v: boardSource?.supply_voltage_min_v,
    supply_voltage_max_v: boardSource?.supply_voltage_max_v,
    logic_voltage_min_v: boardSource?.logic_voltage_min_v,
    logic_voltage_max_v: boardSource?.logic_voltage_max_v,
    current_draw_typ_ma: boardSource?.current_draw_typ_ma,
    current_draw_max_ma: boardSource?.current_draw_max_ma,
    rail_3v3_max_ma: boardSource?.rail_3v3_max_ma,
    rail_5v_max_ma: boardSource?.rail_5v_max_ma,
    supported_protocols: boardSource?.supported_protocols,
    protocol_notes: boardSource?.protocol_notes,
    clock_speed: boardSource?.clock_speed,
    flash_memory: boardSource?.flash_memory,
    ram: boardSource?.ram,
      rom: boardSource?.rom,
      image_url: boardSource?.image_url || '',
      gallery_images: normalizeBoardGalleryImages(boardSource?.gallery_images),
      affiliate_links: normalizeAffiliateLinks(boardSource?.affiliate_links),
      link: boardSource?.link,
      datasheet_url: boardSource?.datasheet_url || '',
      tutorial_urls: normalizeTutorialUrls(boardSource?.tutorial_urls),
      verification_status: normalizeBoardVerificationStatus(boardSource?.verification_status),
      category: boardSource?.category,
    svg_content: boardSource?.svg_content ?? '',
    svg_source_content: boardSource?.svg_source_content ?? calloutSourceSvg,
    svg_display_scale: boardSource?.svg_display_scale ?? DEFAULT_SVG_DISPLAY_SCALE,
    svg_viewbox: workingSvgViewBox,
    svg_rotation_deg: boardSvgRotation,
    public_svg_rotation_deg: publicSvgRotation,
    pin_callouts: pinCalloutValidation.callouts,
    show_mcu_specs: Number(boardSource?.show_mcu_specs) ? 1 : 0,
    ...overrides,
  });

  const buildPublicPinoutPngUpload = async () => {
    if (!publicPreviewSvg) {
      return null;
    }

    return createBoardPublicPinoutImageUpload({
      svgMarkup: publicPreviewSvg,
      pins,
      fileName: `${board?.slug || board?.name || 'board'}-pinout.jpg`,
    });
  };

  const persistPins = async () => {
    const pinsToUpdate = pins || [];

    for (const pin of pinsToUpdate) {
      const position = pinPositions[pin.id];
      const response = await adminFetch(`/admin/pins/${pin.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(createPinRequestPayload(pin, position))
      });

      const result = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(result?.error || `Failed to update pin ${pin.pin_name || pin.pin_number}`);
      }
    }
  };

  const persistBoardDefinition = async () => {
    const currentSvgValidationError = validateSvgMarkup(calloutSourceSvg);
    setSvgValidationError(currentSvgValidationError);

    if (currentSvgValidationError) {
      throw new Error(currentSvgValidationError);
    }

    const affiliateLinksValidationError = validateAffiliateLinks(board?.affiliate_links || []);
    if (affiliateLinksValidationError) {
      throw new Error(affiliateLinksValidationError);
    }

    const tutorialUrlsValidationError = validateTutorialUrls(board?.tutorial_urls || []);
    if (tutorialUrlsValidationError) {
      throw new Error(tutorialUrlsValidationError);
    }

    if (pinCalloutValidation.errors.length > 0) {
      throw new Error(pinCalloutValidation.errors[0]);
    }

    const publicPinoutPngUpload = await buildPublicPinoutPngUpload();
    const { adminBoardsAPI } = await import('../services/adminApi');
    const sourceSvgHasChanged = String(calloutSourceSvg || '') !== String(board?.svg_source_content || '');
    const updateResponse = await adminBoardsAPI.update(
      id,
      buildBoardPayload(board, {
        svg_content: undefined,
        svg_source_content: sourceSvgHasChanged ? calloutSourceSvg : undefined,
      })
    );
    const updatedSlug = updateResponse?.data?.data?.slug || id;
    const updatedBoardId = updateResponse?.data?.data?.id || updatedSlug;
    let publicPinoutPngResponseData = null;
    const rebuiltSvg = updateResponse?.data?.data?.svg_content || '';

    if (publicPinoutPngUpload) {
      const formData = new FormData();
      formData.append('public_pinout_png', publicPinoutPngUpload.blob, publicPinoutPngUpload.fileName);

      try {
        const publicPinoutPngResponse = await adminBoardsAPI.uploadPublicPinoutPng(updatedBoardId, formData);
        publicPinoutPngResponseData = publicPinoutPngResponse?.data?.data || null;
      } catch (uploadError) {
        const uploadErrorMessage = uploadError?.response?.data?.error || uploadError?.message || 'Failed to upload the public pinout PNG.';
        throw new Error(`Board details were saved, but the public pinout PNG upload failed: ${uploadErrorMessage}`);
      }
    }

    setBoard((prevBoard) => ({
      ...prevBoard,
      gallery_images: Array.isArray(updateResponse?.data?.data?.gallery_images)
        ? updateResponse.data.data.gallery_images
        : prevBoard.gallery_images,
      affiliate_links: Array.isArray(updateResponse?.data?.data?.affiliate_links)
        ? updateResponse.data.data.affiliate_links
        : prevBoard.affiliate_links,
      meta_description: updateResponse?.data?.data?.meta_description ?? prevBoard.meta_description,
      seo_keywords: updateResponse?.data?.data?.seo_keywords ?? prevBoard.seo_keywords,
      datasheet_url: updateResponse?.data?.data?.datasheet_url ?? prevBoard.datasheet_url,
      tutorial_urls: Array.isArray(updateResponse?.data?.data?.tutorial_urls)
        ? updateResponse.data.data.tutorial_urls
        : prevBoard.tutorial_urls,
      verification_status: normalizeBoardVerificationStatus(
        updateResponse?.data?.data?.verification_status ?? prevBoard.verification_status
      ),
      updated_at: publicPinoutPngResponseData?.updated_at ?? updateResponse?.data?.data?.updated_at ?? prevBoard.updated_at,
      svg_source_content: updateResponse?.data?.data?.svg_source_content ?? calloutSourceSvg,
      svg_display_scale: updateResponse?.data?.data?.svg_display_scale ?? prevBoard.svg_display_scale,
      svg_viewbox: workingSvgViewBox,
      svg_rotation_deg: updateResponse?.data?.data?.svg_rotation_deg ?? prevBoard.svg_rotation_deg,
      public_svg_rotation_deg: updateResponse?.data?.data?.public_svg_rotation_deg ?? prevBoard.public_svg_rotation_deg,
      has_public_pinout_png: publicPinoutPngResponseData?.has_public_pinout_png ?? updateResponse?.data?.data?.has_public_pinout_png ?? prevBoard.has_public_pinout_png,
      pin_callouts: pinCalloutValidation.callouts,
      svg_content: rebuiltSvg || prevBoard.svg_content
    }));

    if (updatedSlug && updatedSlug !== id) {
      navigate(`/admin/boards/${updatedSlug}/edit`, { replace: true });
    }

    return {
      slug: updatedSlug,
      rebuiltSvg
    };
  };

  const runBoardSave = async ({
    includePins = false,
    successMessage,
    failureMessage,
    closeBoardEditor = false,
  }) => {
    if (saving) {
      return false;
    }

    const boardSnapshotAtSave = currentBoardSnapshot;
    const pinsSnapshotAtSave = currentPinsSnapshot;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      if (includePins) {
        await persistPins();
      }

      await persistBoardDefinition();

      if (boardSnapshotAtSave !== null) {
        setSavedBoardSnapshot(boardSnapshotAtSave);
      }

      if (includePins && pinsSnapshotAtSave !== null) {
        setSavedPinsSnapshot(pinsSnapshotAtSave);
      }

      if (closeBoardEditor) {
        setEditingBoard(false);
      }

      if (successMessage) {
        setSuccess(successMessage);
        setTimeout(() => setSuccess(null), 3000);
      }

      return true;
    } catch (err) {
      setError(getRequestErrorMessage(err, failureMessage));
      console.error(failureMessage, err);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBoard = async () => runBoardSave({
    includePins: false,
    successMessage: 'Board information and callouts updated successfully!',
    failureMessage: 'Failed to update board information. Please try again.',
    closeBoardEditor: true,
  });

  const handleSave = async () => runBoardSave({
    includePins: true,
    successMessage: 'Board pins and callouts saved successfully!',
    failureMessage: 'Failed to save changes',
  });

  const handleSaveAndPublish = async () => {
    if (saving || publishing || !hasUnsavedChanges) {
      return false;
    }

    const publishIdentifier = board?.id || board?.slug || id;

    if (!publishIdentifier) {
      setError('Board changes cannot be published because the board ID is missing.');
      return false;
    }

    setPublishing(true);

    try {
      const saved = await runBoardSave({
        includePins: true,
        failureMessage: 'Failed to save changes before publishing',
      });

      if (!saved) {
        return false;
      }

      const { adminBoardsAPI } = await import('../services/adminApi');
      await adminBoardsAPI.publish(publishIdentifier, true);

      setBoard((prevBoard) => (
        prevBoard
          ? { ...prevBoard, published: 1 }
          : prevBoard
      ));
      setSuccess('Board changes saved and published successfully!');
      setTimeout(() => setSuccess(null), 3000);
      return true;
    } catch (err) {
      setError(getRequestErrorMessage(err, 'Board changes were saved, but publishing failed. Please try again.'));
      console.error('Failed to publish board changes', err);
      return false;
    } finally {
      setPublishing(false);
    }
  };

  const handleRebuildInteractiveSvg = async () => runBoardSave({
    includePins: true,
    successMessage: 'Interactive SVG rebuilt from the current source SVG, pins, and callouts.',
    failureMessage: 'Failed to rebuild interactive SVG',
  });

  const { confirmNavigation: confirmBoardExit } = useUnsavedChangesGuard({
    enabled: hasUnsavedChanges,
    saveMessage: 'You have unsaved board changes. Save them before leaving?',
    discardMessage: 'Leave without saving this board?',
    onSave: handleSave,
  });


  const handleSVGPinClick = (arg) => {
    let payload = (arg && typeof arg === 'object' && 'pin' in arg) ? arg : null;

    if (!payload && arg?.target) {
      const el = arg.target.closest ? arg.target.closest('.pin-rect, .pin-hole, .pin-callout-bubble, .pin-callout-anchor') : null;
      if (el) {
        payload = {
          pin: el.getAttribute('data-pin') || '',
          svgId: el.getAttribute('data-svgid') || el.id || '',
          pinNumber: el.getAttribute('data-pin-number') || ''
        };
      }
    }

    if (!payload || !pins) return;

    const { pin: pinName, svgId, pinNumber } = payload;
    const pin = pins.find((currentPin) =>
      (pinName && currentPin.pin_name === pinName) ||
      (svgId && currentPin.svg_id === svgId) ||
      (pinNumber && String(currentPin.pin_number) === String(pinNumber))
    );

    if (!pin) return;

    focusPinConfig(pin.id);
  };

  const isCalloutEditMode = editMode === 'callouts';
  const isBoardActionBusy = saving || publishing;
  const boardPublicIdentifier = board?.slug || board?.id || id;
  const publicBoardPath = `/boards/${boardPublicIdentifier}`;
  const previewBoardPath = `/boards/preview/${boardPublicIdentifier}`;
  const boardInfoToggleLabel = editingBoard ? 'Cancel Edit' : 'Edit Board Info & SVG';
  const handleExitToPublicBoard = () => {
    if (isBoardActionBusy) {
      return;
    }

    confirmBoardExit(() => navigate(publicBoardPath));
  };
  const handleOpenBoardPreview = () => {
    const previewDraft = {
      board: {
        ...board,
        ...buildBoardPayload(board),
        id: board?.id ?? id,
        slug: board?.slug || '',
        svg_content: applySvgViewBoxToMarkup(
          calloutSourceSvg || board?.svg_content || '',
          workingSvgViewBox
        ) || (calloutSourceSvg || board?.svg_content || ''),
        svg_source_content: calloutSourceSvg,
        pin_callouts: pinCalloutValidation.callouts,
        svg_viewbox: workingSvgViewBox,
      },
      pins: (Array.isArray(pins) ? pins : []).map((pin) => {
        const position = pinPositions?.[pin.id];

        return {
          ...pin,
          position_x: position ? position.x : pin.position_x,
          position_y: position ? position.y : pin.position_y,
        };
      }),
    };
    const previewToken = createBoardPreviewDraft(boardPublicIdentifier, previewDraft);
    const previewUrl = previewToken
      ? `${appUrl(previewBoardPath)}?previewToken=${encodeURIComponent(previewToken)}`
      : appUrl(previewBoardPath);

    window.open(previewUrl, '_blank', 'noopener,noreferrer');
  };
  const boardEditActions = (
    <ActionButtons>
      <Button
        type="button"
        secondary
        onClick={handleOpenBoardPreview}
        title="Preview"
        aria-label="Preview"
      >
        <Eye size={16} />
        <ButtonText>Preview</ButtonText>
      </Button>
      <Button
        type="button"
        secondary
        onClick={() => window.open(appUrl(publicBoardPath), '_blank', 'noopener,noreferrer')}
        title="View Public"
        aria-label="View Public"
      >
        <ExternalLink size={16} />
        <ButtonText>View Public</ButtonText>
      </Button>
      <Button
        type="button"
        danger
        onClick={handleExitToPublicBoard}
        title="Cancel"
        aria-label="Cancel"
      >
        <X size={16} />
        <ButtonText>Cancel</ButtonText>
      </Button>
      <Button
        type="button"
        success
        onClick={handleRebuildInteractiveSvg}
        disabled={isBoardActionBusy}
        title="Rebuild SVG"
        aria-label="Rebuild SVG"
      >
        <RotateCcw size={16} />
        <ButtonText>{saving ? 'Working...' : 'Rebuild SVG'}</ButtonText>
      </Button>
      <Button
        type="button"
        primary
        onClick={handleSave}
        disabled={isBoardActionBusy}
        title="Save Changes"
        aria-label="Save Changes"
      >
        <Save size={16} />
        <ButtonText>{saving ? 'Saving...' : 'Save Changes'}</ButtonText>
      </Button>
      <Button
        type="button"
        success
        onClick={handleSaveAndPublish}
        disabled={isBoardActionBusy || !hasUnsavedChanges}
        title={hasUnsavedChanges ? 'Save Changes and Publish' : 'Publish is available after changes are made'}
        aria-label="Publish"
      >
        <Globe size={16} />
        <ButtonText>{publishing ? 'Publishing...' : 'Publish'}</ButtonText>
      </Button>
    </ActionButtons>
  );

  if (loading) {
    return (
      <EditBoardContainer>
        <LoadingSpinner>Loading board data...</LoadingSpinner>
      </EditBoardContainer>
    );
  }

  if (!board) {
    return (
      <EditBoardContainer>
        <ErrorMessage>{error || 'Board not found'}</ErrorMessage>
      </EditBoardContainer>
    );
  }

  return (
    <EditBoardContainer>
      <BackButton onClick={handleExitToPublicBoard}>
        <ArrowLeft size={20} />
        Back to Board
      </BackButton>

      <Header>
        <Title>Edit Board: {board.name}</Title>
        <Subtitle>Edit pin metadata, board details, source SVG, detected pin labels, and custom callouts</Subtitle>
      </Header>

      {(error || success) && (
        <NotificationStack aria-live="polite" aria-atomic="true">
          {error && <FloatingErrorMessage>{error}</FloatingErrorMessage>}
          {success && <FloatingSuccessMessage>{success}</FloatingSuccessMessage>}
        </NotificationStack>
      )}

      <EditorWorkspace>
        <ActionRail>
          {boardEditActions}
        </ActionRail>

        <MainColumn>
          <BoardInfoSection>
            <BoardInfoHeader>
              <BoardInfoTitle>Board Information</BoardInfoTitle>
              <EditBoardButton 
                onClick={() => setEditingBoard(!editingBoard)}
                disabled={saving}
              >
                <Edit3 size={16} />
                {boardInfoToggleLabel}
              </EditBoardButton>
            </BoardInfoHeader>

            <BoardInfoGrid>
              <BoardInfoField>
                <BoardInfoLabel>Board Name</BoardInfoLabel>
                {editingBoard ? (
                  <BoardInfoInput
                    type="text"
                    value={board.name || ''}
                    onChange={(e) => handleBoardUpdate('name', e.target.value)}
                    placeholder="Enter board name"
                  />
                ) : (
                  <BoardInfoDisplay>{board.name || 'Not specified'}</BoardInfoDisplay>
                )}
              </BoardInfoField>

          <BoardInfoField>
            <BoardInfoLabel>Manufacturer</BoardInfoLabel>
            {editingBoard ? (
              <BoardInfoInput
                type="text"
                value={board.manufacturer || ''}
                onChange={(e) => handleBoardUpdate('manufacturer', e.target.value)}
                placeholder="Enter manufacturer"
              />
            ) : (
              <BoardInfoDisplay>{board.manufacturer || 'Not specified'}</BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField>
            <BoardInfoLabel>Package Type</BoardInfoLabel>
            {editingBoard ? (
              <BoardInfoInput
                type="text"
                value={board.package_type || ''}
                onChange={(e) => handleBoardUpdate('package_type', e.target.value)}
                placeholder="Enter package type"
              />
            ) : (
              <BoardInfoDisplay>{board.package_type || 'Not specified'}</BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField>
            <BoardInfoLabel>CPU</BoardInfoLabel>
            {editingBoard ? (
              <BoardInfoInput
                type="text"
                value={board.cpu || ''}
                onChange={(e) => handleBoardUpdate('cpu', e.target.value)}
                placeholder="e.g., ATmega328P, ESP32-S3"
              />
            ) : (
              <BoardInfoDisplay>{board.cpu || 'Not specified'}</BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField>
            <BoardInfoLabel>Number of Pins</BoardInfoLabel>
            {editingBoard ? (
              <BoardInfoInput
                type="number"
                min="1"
                value={board.pin_count || ''}
                onChange={(e) => handleBoardUpdate('pin_count', parseInt(e.target.value) || 0)}
                placeholder="Enter number of pins"
              />
            ) : (
              <BoardInfoDisplay>{board.pin_count || 'Not specified'}</BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField>
            <BoardInfoLabel>Category</BoardInfoLabel>
            {editingBoard ? (
              <BoardInfoSelect
                value={board.category || ''}
                onChange={(e) => handleBoardUpdate('category', e.target.value)}
              >
                <option value="">Select a category...</option>
                {categories.map(category => (
                  <option key={category.key} value={category.key}>
                    {category.icon} {category.name}
                  </option>
                ))}
              </BoardInfoSelect>
            ) : (
              <BoardInfoDisplay>
                {board.category ? (
                  categories.find(cat => cat.key === board.category)?.name || board.category
                ) : 'Not specified'}
              </BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField>
            <BoardInfoLabel>Supply Voltage Min (V)</BoardInfoLabel>
            {editingBoard ? (
              <BoardInfoInput
                type="number"
                min="0"
                step="0.001"
                value={board.supply_voltage_min_v || ''}
                onChange={(e) => handleBoardUpdate('supply_voltage_min_v', e.target.value)}
                placeholder="e.g., 3.3"
              />
            ) : (
              <BoardInfoDisplay>{board.supply_voltage_min_v || 'Not specified'}</BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField>
            <BoardInfoLabel>Supply Voltage Max (V)</BoardInfoLabel>
            {editingBoard ? (
              <BoardInfoInput
                type="number"
                min="0"
                step="0.001"
                value={board.supply_voltage_max_v || ''}
                onChange={(e) => handleBoardUpdate('supply_voltage_max_v', e.target.value)}
                placeholder="e.g., 5"
              />
            ) : (
              <BoardInfoDisplay>{board.supply_voltage_max_v || 'Not specified'}</BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField>
            <BoardInfoLabel>Logic Voltage Min (V)</BoardInfoLabel>
            {editingBoard ? (
              <BoardInfoInput
                type="number"
                min="0"
                step="0.001"
                value={board.logic_voltage_min_v || ''}
                onChange={(e) => handleBoardUpdate('logic_voltage_min_v', e.target.value)}
                placeholder="e.g., 1.8"
              />
            ) : (
              <BoardInfoDisplay>{board.logic_voltage_min_v || 'Not specified'}</BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField>
            <BoardInfoLabel>Logic Voltage Max (V)</BoardInfoLabel>
            {editingBoard ? (
              <BoardInfoInput
                type="number"
                min="0"
                step="0.001"
                value={board.logic_voltage_max_v || ''}
                onChange={(e) => handleBoardUpdate('logic_voltage_max_v', e.target.value)}
                placeholder="e.g., 3.3"
              />
            ) : (
              <BoardInfoDisplay>{board.logic_voltage_max_v || 'Not specified'}</BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField>
            <BoardInfoLabel>Typical Current Draw (mA)</BoardInfoLabel>
            {editingBoard ? (
              <BoardInfoInput
                type="number"
                min="0"
                step="0.001"
                value={board.current_draw_typ_ma || ''}
                onChange={(e) => handleBoardUpdate('current_draw_typ_ma', e.target.value)}
                placeholder="e.g., 0.6"
              />
            ) : (
              <BoardInfoDisplay>{board.current_draw_typ_ma || 'Not specified'}</BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField>
            <BoardInfoLabel>Max Current Draw (mA)</BoardInfoLabel>
            {editingBoard ? (
              <BoardInfoInput
                type="number"
                min="0"
                step="0.001"
                value={board.current_draw_max_ma || ''}
                onChange={(e) => handleBoardUpdate('current_draw_max_ma', e.target.value)}
                placeholder="e.g., 1.2"
              />
            ) : (
              <BoardInfoDisplay>{board.current_draw_max_ma || 'Not specified'}</BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField>
            <BoardInfoLabel>3.3V Rail Limit (mA)</BoardInfoLabel>
            {editingBoard ? (
              <BoardInfoInput
                type="number"
                min="0"
                step="0.001"
                value={board.rail_3v3_max_ma || ''}
                onChange={(e) => handleBoardUpdate('rail_3v3_max_ma', e.target.value)}
                placeholder="For MCU/dev boards"
              />
            ) : (
              <BoardInfoDisplay>{board.rail_3v3_max_ma || 'Not specified'}</BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField>
            <BoardInfoLabel>5V Rail Limit (mA)</BoardInfoLabel>
            {editingBoard ? (
              <BoardInfoInput
                type="number"
                min="0"
                step="0.001"
                value={board.rail_5v_max_ma || ''}
                onChange={(e) => handleBoardUpdate('rail_5v_max_ma', e.target.value)}
                placeholder="For MCU/dev boards"
              />
            ) : (
              <BoardInfoDisplay>{board.rail_5v_max_ma || 'Not specified'}</BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField style={{ gridColumn: '1 / -1' }}>
            <BoardInfoLabel>Public Spec Cards</BoardInfoLabel>
            {editingBoard ? (
              <>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={Boolean(Number(board.show_mcu_specs))}
                    onChange={(e) => handleBoardUpdate('show_mcu_specs', e.target.checked ? 1 : 0)}
                  />
                  Show MCU-only specs: CPU, Clock Speed, Flash Memory, ROM, RAM
                </label>
                <BoardInfoHint>
                  Turn this off for sensors/modules to avoid empty public spec cards.
                </BoardInfoHint>
              </>
            ) : (
              <BoardInfoDisplay>
                {Number(board.show_mcu_specs) ? 'Shown' : 'Hidden'}
              </BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField>
            <BoardInfoLabel>Clock Speed</BoardInfoLabel>
            {editingBoard ? (
              <BoardInfoInput
                type="text"
                value={board.clock_speed || ''}
                onChange={(e) => handleBoardUpdate('clock_speed', e.target.value)}
                placeholder="e.g., 16MHz, 48MHz"
              />
            ) : (
              <BoardInfoDisplay>{board.clock_speed || 'Not specified'}</BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField>
            <BoardInfoLabel>Flash Memory</BoardInfoLabel>
            {editingBoard ? (
              <BoardInfoInput
                type="text"
                value={board.flash_memory || ''}
                onChange={(e) => handleBoardUpdate('flash_memory', e.target.value)}
                placeholder="e.g., 32KB, 256KB"
              />
            ) : (
              <BoardInfoDisplay>{board.flash_memory || 'Not specified'}</BoardInfoDisplay>
            )}
          </BoardInfoField>

            <BoardInfoField>
              <BoardInfoLabel>RAM</BoardInfoLabel>
              {editingBoard ? (
                <BoardInfoInput
                  type="text"
                value={board.ram || ''}
                onChange={(e) => handleBoardUpdate('ram', e.target.value)}
                placeholder="e.g., 2KB, 32KB"
              />
              ) : (
                <BoardInfoDisplay>{board.ram || 'Not specified'}</BoardInfoDisplay>
              )}
            </BoardInfoField>

          <BoardInfoField>
            <BoardInfoLabel>ROM</BoardInfoLabel>
            {editingBoard ? (
              <BoardInfoInput
                type="text"
                value={board.rom || ''}
                onChange={(e) => handleBoardUpdate('rom', e.target.value)}
                placeholder="e.g., 8KB, 64KB"
              />
            ) : (
              <BoardInfoDisplay>{board.rom || 'Not specified'}</BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField style={{ gridColumn: '1 / -1' }}>
            <BoardInfoLabel>Supported Protocols</BoardInfoLabel>
            {editingBoard ? (
              <>
                <CheckboxGrid>
                  {PROTOCOL_OPTIONS.map((protocol) => (
                    <CheckboxChip key={protocol.value}>
                      <input
                        type="checkbox"
                        checked={(board.supported_protocols || []).includes(protocol.value)}
                        onChange={() => handleBoardProtocolToggle(protocol.value)}
                      />
                      <span>{protocol.label}</span>
                    </CheckboxChip>
                  ))}
                </CheckboxGrid>
                <BoardInfoHint>
                  Choose the protocols this board exposes or can operate over in a normal wiring setup.
                </BoardInfoHint>
              </>
            ) : (
              <BoardInfoDisplay>
                {(board.supported_protocols || []).length > 0
                  ? board.supported_protocols.join(', ')
                  : 'Not specified'}
              </BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField style={{ gridColumn: '1 / -1' }}>
            <BoardInfoLabel>Protocol Notes</BoardInfoLabel>
            {editingBoard ? (
              <TextArea
                value={board.protocol_notes || ''}
                onChange={(e) => handleBoardUpdate('protocol_notes', e.target.value)}
                placeholder="Optional notes about addresses, onboard pull-ups, selectable modes, or level-shifting caveats..."
              />
            ) : (
              <BoardInfoDisplay>{board.protocol_notes || 'No notes yet'}</BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField style={{ gridColumn: '1 / -1' }}>
            <BoardInfoLabel>Documentation URL</BoardInfoLabel>
            {editingBoard ? (
              <BoardInfoInput
                type="url"
                value={board.link || ''}
                onChange={(e) => handleBoardUpdate('link', e.target.value)}
                placeholder="https://example.com/board-documentation"
              />
            ) : (
              <BoardInfoDisplay>
                {board.link ? (
                  <a href={board.link} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                    {board.link}
                  </a>
                ) : (
                  'No link provided'
                )}
              </BoardInfoDisplay>
            )}
          </BoardInfoField>

            <BoardInfoField style={{ gridColumn: '1 / -1' }}>
              <BoardInfoLabel>Datasheet URL</BoardInfoLabel>
              {editingBoard ? (
                <BoardInfoInput
                  type="url"
                value={board.datasheet_url || ''}
                onChange={(e) => handleBoardUpdate('datasheet_url', e.target.value)}
                placeholder="https://..."
              />
            ) : (
              <BoardInfoDisplay>
                {board.datasheet_url ? (
                  <a href={board.datasheet_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                    {board.datasheet_url}
                  </a>
                ) : (
                  'No datasheet provided'
                )}
                </BoardInfoDisplay>
              )}
            </BoardInfoField>

            {(editingBoard || boardTutorialUrls.length > 0) && (
              <BoardInfoField style={{ gridColumn: '1 / -1' }}>
                <BoardInfoLabel>Tutorials</BoardInfoLabel>
                {editingBoard ? (
                  <TutorialUrlsEditor
                    value={board.tutorial_urls}
                    onChange={(tutorialUrls) => handleBoardUpdate('tutorial_urls', tutorialUrls)}
                    disabled={saving}
                    itemNameSingular="tutorial URL"
                    itemNamePlural="tutorial URLs"
                    addButtonLabel="Add Tutorial URL"
                    helperText="Add one or more tutorial URLs for this board."
                    emptyStateText="No tutorial URLs added yet."
                    urlPlaceholder="https://example.com/tutorial"
                  />
                ) : (
                  <BoardAffiliateLinksList>
                    {boardTutorialUrls.map((tutorialUrl, index) => (
                      <BoardAffiliateLinkItem
                        key={`board-tutorial-url-${index}`}
                        href={tutorialUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <BoardAffiliateLinkLabel>
                          {boardTutorialUrls.length > 1 ? `Tutorial ${index + 1}` : 'Tutorial'}
                        </BoardAffiliateLinkLabel>
                        <BoardAffiliateLinkUrl>{tutorialUrl}</BoardAffiliateLinkUrl>
                      </BoardAffiliateLinkItem>
                    ))}
                  </BoardAffiliateLinksList>
                )}
              </BoardInfoField>
            )}

            <BoardInfoField>
              <BoardInfoLabel>Verification Status</BoardInfoLabel>
            {editingBoard ? (
              <BoardInfoSelect
                value={normalizeBoardVerificationStatus(board.verification_status)}
                onChange={(e) => handleBoardUpdate('verification_status', e.target.value)}
              >
                {BOARD_VERIFICATION_STATUS_OPTIONS.map((statusOption) => (
                  <option key={statusOption.value} value={statusOption.value}>
                    {statusOption.label}
                  </option>
                ))}
              </BoardInfoSelect>
            ) : (
              <BoardInfoDisplay>{getBoardVerificationLabel(board.verification_status)}</BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField style={{ gridColumn: '1 / -1' }}>
            <BoardInfoLabel>Affiliate Links</BoardInfoLabel>
            {editingBoard ? (
              <AffiliateLinksEditor
                value={board.affiliate_links}
                onChange={(affiliateLinks) => handleBoardUpdate('affiliate_links', affiliateLinks)}
                disabled={saving}
              />
            ) : (
              normalizeAffiliateLinks(board.affiliate_links).length > 0 ? (
                <BoardAffiliateLinksList>
                  {normalizeAffiliateLinks(board.affiliate_links).map((affiliateLink, index) => (
                    <BoardAffiliateLinkItem
                      key={`board-affiliate-link-${index}`}
                      href={affiliateLink.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <BoardAffiliateLinkLabel>{affiliateLink.label}</BoardAffiliateLinkLabel>
                      <BoardAffiliateLinkUrl>{affiliateLink.url}</BoardAffiliateLinkUrl>
                    </BoardAffiliateLinkItem>
                  ))}
                </BoardAffiliateLinksList>
              ) : (
                <BoardInfoDisplay>No links yet</BoardInfoDisplay>
              )
            )}
          </BoardInfoField>

          <BoardInfoField style={{ gridColumn: '1 / -1' }}>
            <BoardInfoLabel>Description</BoardInfoLabel>
            {editingBoard ? (
              <RichContentEditor
                htmlValue={board.description || ''}
                onChange={(value) => handleBoardUpdate('description', value)}
                richTextPlaceholder="Enter board description with formatting..."
                htmlSourcePlaceholder="<p>Enter the board description HTML here...</p>"
                markdownPlaceholder={`# Board overview

Use markdown here.

| Pin group | Notes |
| --- | --- |
| Power | Voltage and current details |
| GPIO | Helpful caveats or defaults |`}
                conversionNote="Switch between Rich Text, raw HTML, Markdown, and Preview. The saved board description stays HTML, so Markdown and Rich Text both flow into the published board page."
                htmlHint="Use raw HTML here when you need markup the rich-text editor cannot safely preserve, such as custom tables."
                tableProtectionHint="This board description currently contains a table. Rich Text mode is temporarily disabled because it can strip table markup. Keep editing the table in HTML or Markdown until you remove it."
                markdownHint="Use headings, lists, links, blockquotes, code blocks, and GitHub-style tables here. The saved board description is still converted into HTML automatically."
                previewHint="This is the rendered board description preview."
                previewEmptyState="Nothing to preview yet. Start writing in Rich Text, HTML, or Markdown and the rendered board description will appear here."
                height={220}
                disabled={saving}
              />
            ) : (
              <BoardRichContentDisplay dangerouslySetInnerHTML={{ __html: board.description || 'No description available' }} />
            )}
          </BoardInfoField>

          <BoardInfoField style={{ gridColumn: '1 / -1' }}>
            <BoardInfoFieldHeader>
              <BoardInfoLabel>SEO Meta Description</BoardInfoLabel>
              {editingBoard && (
                <BoardInfoInlineButton
                  type="button"
                  onClick={handleRegenerateMetaDescription}
                  disabled={saving}
                >
                  <RotateCcw size={14} />
                  Regenerate From Description
                </BoardInfoInlineButton>
              )}
            </BoardInfoFieldHeader>
            {editingBoard ? (
              <>
                <TextArea
                  value={board.meta_description || ''}
                  onChange={(e) => handleBoardUpdate('meta_description', e.target.value)}
                  placeholder="Short plain-text summary used for the public page meta description..."
                />
                <BoardInfoHint>
                  Search engines usually show around 150 to 160 characters here.
                </BoardInfoHint>
              </>
            ) : (
              <BoardInfoDisplay>{board.meta_description || 'No SEO meta description set'}</BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField style={{ gridColumn: '1 / -1' }}>
            <BoardInfoLabel>SEO Keywords</BoardInfoLabel>
            {editingBoard ? (
              <>
                <TextArea
                  value={board.seo_keywords || ''}
                  onChange={(e) => handleBoardUpdate('seo_keywords', e.target.value)}
                  placeholder="arduino nano, pinout, atmega328p, development board"
                />
                <BoardInfoHint>
                  Separate keywords with commas.
                </BoardInfoHint>
              </>
            ) : (
              <BoardInfoDisplay>{board.seo_keywords || 'No SEO keywords set'}</BoardInfoDisplay>
            )}
          </BoardInfoField>

          <BoardInfoField style={{ gridColumn: '1 / -1' }}>
            <BoardInfoLabel>Image Gallery</BoardInfoLabel>
            {editingBoard ? (
              <BoardGalleryEditor
                value={board.gallery_images}
                onChange={(galleryImages) => handleBoardUpdate('gallery_images', galleryImages)}
                disabled={saving}
              />
            ) : (
              normalizeBoardGalleryImages(board.gallery_images).length > 0 ? (
                <GalleryPreviewGrid>
                  {normalizeBoardGalleryImages(board.gallery_images).map((image, index) => (
                    <GalleryPreviewLink
                      key={`gallery-preview-${index}`}
                      href={resolveBoardGalleryImageSrc(image)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <GalleryPreviewImage
                        src={resolveBoardGalleryImageSrc(image)}
                        alt={getBoardGalleryImageCaption(image) || `${board.name || 'Board'} gallery ${index + 1}`}
                        loading="lazy"
                      />
                      <GalleryPreviewCaption>{getBoardGalleryImageCaption(image) || `Image ${index + 1}`}</GalleryPreviewCaption>
                    </GalleryPreviewLink>
                  ))}
                </GalleryPreviewGrid>
              ) : (
                <BoardInfoDisplay>No gallery images uploaded</BoardInfoDisplay>
              )
            )}
          </BoardInfoField>

          <BoardInfoField style={{ gridColumn: '1 / -1' }}>
            <BoardInfoLabel>SVG Source</BoardInfoLabel>
            {editingBoard ? (
              <>
                <CodeEditorTextArea
                  value={calloutSourceSvg}
                  onChange={(e) => handleBoardUpdate('svg_source_content', e.target.value)}
                  placeholder="<svg xmlns=&quot;http://www.w3.org/2000/svg&quot; ...>..."
                  spellCheck={false}
                />
                <CodeHelperText>
                  This is the source SVG used to rebuild the published interactive diagram and the manual callout layout. Keep connector IDs and pin-related attributes stable so the saved labels stay linked to the right pins.
                </CodeHelperText>
                <ValidationMessage $invalid={Boolean(svgValidationError)}>
                  {svgValidationError
                    ? svgValidationError
                    : `SVG looks valid. ${calloutSourceSvg ? calloutSourceSvg.length.toLocaleString() : 0} characters ready to save.`}
                </ValidationMessage>
              </>
            ) : (
              <BoardInfoDisplay>
                {calloutSourceSvg
                  ? `SVG source available (${calloutSourceSvg.length.toLocaleString()} characters)`
                  : 'No SVG source saved for this board'}
              </BoardInfoDisplay>
            )}
          </BoardInfoField>

        </BoardInfoGrid>

        {!editingBoard && (
          <BoardInfoFooterRow>
            <EditBoardButton
              type="button"
              onClick={() => setEditingBoard(true)}
              disabled={saving}
            >
              <Edit3 size={16} />
              {boardInfoToggleLabel}
            </EditBoardButton>
          </BoardInfoFooterRow>
        )}

        {editingBoard && (
          <BoardInfoActions>
            <BoardInfoButton 
              className="secondary"
              onClick={() => setEditingBoard(false)}
              disabled={saving}
            >
              <X size={16} />
              Cancel
            </BoardInfoButton>
            <BoardInfoButton 
              className="primary"
              onClick={handleSaveBoard}
              disabled={saving}
            >
              <Save size={16} />
              {saving ? 'Saving...' : 'Save Board Info & SVG'}
            </BoardInfoButton>
          </BoardInfoActions>
        )}
          </BoardInfoSection>

          <ContentGrid $calloutMode={isCalloutEditMode}>
        <BoardPreview>
          <PreviewHeader>
            <PreviewTitle>Board Preview</PreviewTitle>
          </PreviewHeader>
          
          <EditModeToggle>
            <ToggleButton
              active={editMode === 'pins'}
              onClick={() => setEditMode('pins')}
            >
              <Edit3 size={16} />
              Edit Pins
            </ToggleButton>
            <ToggleButton
              active={editMode === 'callouts'}
              onClick={() => {
                setEditingPinName(null);
                setEditMode('callouts');
              }}
            >
              <Edit3 size={16} />
              Edit Callouts
            </ToggleButton>
          </EditModeToggle>

          {editMode === 'pins' && (
            <PublicSvgControlsPanel>
              <PublicSvgControlsTitle>Public SVG Framing</PublicSvgControlsTitle>
              <BoardInfoHint>
                Adjust the public board zoom, offset, and board-detail rotation here while you work on pin placement. This rotation only affects the public board detail page.
              </BoardInfoHint>
              <PublicSvgControlsGrid style={{ marginTop: '16px' }}>
                <BoardInfoField>
                  <BoardInfoLabel>Public SVG Scale</BoardInfoLabel>
                  <>
                    <BoardInfoInput
                      type="number"
                      min="0.25"
                      max="3"
                      step="0.05"
                      value={publicSvgScaleDraft}
                      onChange={(e) => handlePublicSvgScaleDraftChange(e.target.value)}
                      onBlur={handlePublicSvgScaleDraftBlur}
                      disabled={saving}
                      placeholder="1.00"
                    />
                    <CodeHelperText>
                      Use smaller values to make this board render less aggressively in public, compare, and embed views.
                    </CodeHelperText>
                  </>
                </BoardInfoField>

                <BoardInfoField>
                  <BoardInfoLabel>Public SVG Position</BoardInfoLabel>
                  <>
                    <BoardInfoInlineFields>
                      <BoardInfoMiniField>
                        <BoardInfoLabel>Offset X</BoardInfoLabel>
                        <BoardInfoInput
                          type="number"
                          step="1"
                          value={publicSvgOffsetDraft.x}
                          onChange={(e) => handlePublicSvgOffsetDraftChange('x', e.target.value)}
                          onBlur={handlePublicSvgOffsetDraftBlur}
                          disabled={saving}
                          placeholder="0"
                        />
                      </BoardInfoMiniField>
                      <BoardInfoMiniField>
                        <BoardInfoLabel>Offset Y</BoardInfoLabel>
                        <BoardInfoInput
                          type="number"
                          step="1"
                          value={publicSvgOffsetDraft.y}
                          onChange={(e) => handlePublicSvgOffsetDraftChange('y', e.target.value)}
                          onBlur={handlePublicSvgOffsetDraftBlur}
                          disabled={saving}
                          placeholder="0"
                        />
                      </BoardInfoMiniField>
                    </BoardInfoInlineFields>
                    <CodeHelperText>
                      Positive X moves the board right. Positive Y moves the board down. This keeps the current public zoom and only shifts the framing before the board-detail rotation is applied.
                    </CodeHelperText>
                  </>
                </BoardInfoField>

                <BoardInfoField>
                  <BoardInfoLabel>Board Detail Rotation</BoardInfoLabel>
                  <>
                    <BoardInfoSelect
                      value={publicSvgRotation}
                      onChange={(e) => handlePublicSvgRotationChange(e.target.value)}
                      disabled={saving}
                    >
                      <option value="0">0°</option>
                      <option value="90">90°</option>
                      <option value="180">180°</option>
                      <option value="270">270°</option>
                    </BoardInfoSelect>
                    <CodeHelperText>
                      This is separate from the board SVG rotation used for pin placement, wiring, and Builder.
                    </CodeHelperText>
                  </>
                </BoardInfoField>
              </PublicSvgControlsGrid>
            </PublicSvgControlsPanel>
          )}

          <div style={{ position: 'relative' }} ref={svgContainerRef}>
            {editMode === 'callouts' ? (
              <>
                {!calloutSourceSvg && (
                  <CalloutNotice $tone="warning">
                    This board does not have a saved source SVG yet. Add or restore the source SVG in the Board Information section before editing callouts.
                  </CalloutNotice>
                )}

                {pinCalloutValidation.errors.length > 0 && (
                  <CalloutNotice $tone="warning">
                    {pinCalloutValidation.errors[0]}
                  </CalloutNotice>
                )}

                {pinCalloutValidation.warnings.length > 0 && (
                  <CalloutNotice>
                    {pinCalloutValidation.warnings.join(' ')}
                  </CalloutNotice>
                )}

                {calloutSourceSvg && (
                <PinoutCalloutEditor
                  svgContent={calloutSourceSvg}
                  referenceSvgContent={calloutViewportReferenceSvg}
                  referenceDisplayScale={board?.svg_display_scale ?? DEFAULT_SVG_DISPLAY_SCALE}
                  initialViewBox={editorWorkingSvgViewBox}
                  pins={pins}
                  pinGroups={pinGroups}
                  rotation={calloutEditorRotation}
                  callouts={editorPinCallouts}
                  onRotationChange={handleCalloutRotationChange}
                  onCalloutsChange={handleEditorCalloutsChange}
                  disabled={saving}
                />
                )}
              </>
            ) : (
              <BoardPublicSvgPreview
                svgContent={publicPreviewSvg}
                pins={pins}
                displayScale={board?.svg_display_scale ?? DEFAULT_SVG_DISPLAY_SCALE}
                maxWidth="100%"
                showAlternatePinCalloutColors={true}
                onPinClick={handleSVGPinClick}
              />
            )}
          </div>
        </BoardPreview>

        {!isCalloutEditMode && (
        <PinEditor>
            <EditorTitle>
              Pin Configuration
              <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#64748b' }}>
                {(pins || []).length} pins
              </span>
            </EditorTitle>
            
            <div style={{
              background: '#f0f9ff',
              border: '1px solid #0ea5e9',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px',
              fontSize: '14px',
              color: '#0c4a6e'
            }}>
              <strong>Pin Repair:</strong> Adjust pin names, categories, and saved X/Y coordinates, then use <strong>Rebuild SVG</strong> to regenerate clickable markup from the current board data.
            </div>

            <PinBulkElectricalDefaultsPanel
              values={bulkElectricalValues}
              applyToAll={applyBulkElectricalValues}
              onValueChange={handleBulkElectricalValueChange}
              onApplyToAllChange={handleApplyBulkElectricalValuesChange}
              applyCount={bulkEligiblePinCount}
              disabled={saving}
            />

            <PinList>
              {(pins || []).map(pin => (
                <PinItem key={pin.id} id={`pin-${pin.id}`}>
                  <PinHeader>
                    <PinTitleRow>
                      {editingPinName === pin.id ? (
                        <PinNameInput
                          value={pin.pin_name || ''}
                          onChange={(e) => handlePinUpdate(pin.id, 'pin_name', e.target.value)}
                          onBlur={() => setEditingPinName(null)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              setEditingPinName(null);
                            }
                            if (e.key === 'Escape') {
                              setEditingPinName(null);
                            }
                          }}
                          autoFocus
                        />
                      ) : (
                        <>
                          <PinNameDisplay>
                            {pin.pin_name || `Pin ${pin.pin_number}`}
                          </PinNameDisplay>
                          <PinNameEditButton
                            type="button"
                            onClick={() => setEditingPinName(pin.id)}
                            aria-label={`Rename ${pin.pin_name || `Pin ${pin.pin_number}`}`}
                            title="Rename pin"
                          >
                            <Edit3 />
                          </PinNameEditButton>
                        </>
                      )}
                    </PinTitleRow>
                    <PinNumber>#{pin.pin_number}</PinNumber>
                  </PinHeader>
                  
                  <PinDetails>
                    <FormGroup>
                      <Label>Category</Label>
                      <Select
                        value={pin.pin_group_id || ''}
                        onChange={(e) => handlePinUpdate(pin.id, 'pin_group_id', e.target.value ? parseInt(e.target.value) : null)}
                      >
                        <option value="">Select Category</option>
                        {pinGroups.map(group => (
                          <option key={group.id} value={group.id}>
                            {group.name}
                          </option>
                        ))}
                      </Select>
                    </FormGroup>

                    <FormGroup>
                      <Label>Pin Role</Label>
                      <Select
                        value={pin.pin_role || ''}
                        onChange={(e) => handlePinUpdate(pin.id, 'pin_role', e.target.value)}
                      >
                        {PIN_ROLE_OPTIONS.map((option) => (
                          <option key={option.value || 'empty'} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </FormGroup>

                    <FormGroup>
                      <Label>Current Limit (mA)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.001"
                        value={pin.current_limit_ma ?? ''}
                        onChange={(e) => handlePinUpdate(pin.id, 'current_limit_ma', e.target.value)}
                        placeholder="e.g., 20"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Voltage Min (V)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.001"
                        value={pin.voltage_min_v ?? ''}
                        onChange={(e) => handlePinUpdate(pin.id, 'voltage_min_v', e.target.value)}
                        placeholder="e.g., 0"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Voltage Max (V)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.001"
                        value={pin.voltage_max_v ?? ''}
                        onChange={(e) => handlePinUpdate(pin.id, 'voltage_max_v', e.target.value)}
                        placeholder="e.g., 3.3"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>5V Tolerant</Label>
                      <CheckboxChip style={{ marginTop: '2px' }}>
                        <input
                          type="checkbox"
                          checked={Boolean(pin.is_5v_tolerant)}
                          onChange={(e) => handlePinUpdate(pin.id, 'is_5v_tolerant', e.target.checked)}
                        />
                        <span>Safe for 5V logic</span>
                      </CheckboxChip>
                    </FormGroup>

                    <FormGroup>
                      <Label>Pin With Caution</Label>
                      <CheckboxChip style={{ marginTop: '2px' }}>
                        <input
                          type="checkbox"
                          checked={Boolean(pin.use_with_caution)}
                          onChange={(e) => handlePinUpdate(pin.id, 'use_with_caution', e.target.checked)}
                        />
                        <span>Include in public caution list</span>
                      </CheckboxChip>
                    </FormGroup>

                    <FormGroup style={{ gridColumn: '1 / -1' }}>
                      <Label>Primary Function</Label>
                      <Input
                        value={pin.functions || ''}
                        onChange={(e) => handlePinUpdate(pin.id, 'functions', e.target.value)}
                        placeholder="e.g., Digital I/O"
                      />
                    </FormGroup>

                    <FormGroup style={{ gridColumn: '1 / -1' }}>
                      <Label>Capability Tags</Label>
                      <CapabilityTagsField
                        value={pin.capability_tags}
                        onChange={(nextTags) => handlePinUpdate(pin.id, 'capability_tags', nextTags)}
                        suggestedTags={SUGGESTED_PIN_CAPABILITY_TAGS}
                        placeholder="e.g., i2c-sda, gpio, interrupt"
                        inputAriaLabel={`Capability tags for pin ${pin.pin_number || pin.pin_name || pin.id}`}
                        helperText="Type free-form tags, or click a suggested tag to insert it."
                      />
                    </FormGroup>

                    {Boolean(pin.use_with_caution) && (
                      <FormGroup style={{ gridColumn: '1 / -1' }}>
                        <Label>Caution Reason</Label>
                        <TextArea
                          value={pin.caution_reason || ''}
                          onChange={(e) => handlePinUpdate(pin.id, 'caution_reason', e.target.value)}
                          placeholder="Explain why this pin should be used carefully..."
                        />
                      </FormGroup>
                    )}

                    <FormGroup style={{ gridColumn: '1 / -1' }}>
                      <Label>Alternate Names</Label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px' }}>
                        {getEditablePinAlternateNameEntries(pin).map((alternateNameEntry, index) => {
                          const positionSuggestion = alternateNamePositionSuggestionsByPinId[String(pin?.id ?? '')]?.[index] || null;

                          return (
                          <div
                            key={`pin-${pin.id}-alt-name-${index}`}
                            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                          >
                            <Input
                              type="text"
                              value={alternateNameEntry.name}
                              onChange={(e) => handlePinAlternateNameChange(pin.id, index, 'name', e.target.value)}
                              placeholder={`Alternate name ${index + 1}`}
                            />
                            <details
                              defaultOpen={Boolean(alternateNameEntry.x || alternateNameEntry.y)}
                              style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px 12px', background: '#f8fafc' }}
                            >
                              <summary style={{ cursor: 'pointer', color: '#475569', fontSize: '13px', fontWeight: 600 }}>
                                Optional position override
                              </summary>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', marginTop: '10px' }}>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={alternateNameEntry.x}
                                  onChange={(e) => handlePinAlternateNameChange(pin.id, index, 'x', e.target.value)}
                                  placeholder={formatSuggestedAlternateNameCoordinate(positionSuggestion?.x) || 'X'}
                                />
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={alternateNameEntry.y}
                                  onChange={(e) => handlePinAlternateNameChange(pin.id, index, 'y', e.target.value)}
                                  placeholder={formatSuggestedAlternateNameCoordinate(positionSuggestion?.y) || 'Y'}
                                />
                              </div>
                              {positionSuggestion && (
                                <div style={{ marginTop: '8px', fontSize: '12px', color: '#64748b' }}>
                                  Suggested default: x {formatSuggestedAlternateNameCoordinate(positionSuggestion.x)}, y {formatSuggestedAlternateNameCoordinate(positionSuggestion.y)}
                                </div>
                              )}
                            </details>
                            <AltNameCalloutColorSection>
                              <AltNameCalloutColorLabel>Callout Color</AltNameCalloutColorLabel>
                              <AltNameCalloutColorRow>
                                {ALT_PIN_CALLOUT_COLOR_OPTIONS.map((colorOption) => (
                                  <AltNameCalloutColorButton
                                    key={`${pin.id}-${index}-${colorOption.value}`}
                                    type="button"
                                    $selected={alternateNameEntry.color === colorOption.value}
                                    $swatch={colorOption.fill}
                                    onClick={() => handlePinAlternateNameChange(pin.id, index, 'color', colorOption.value)}
                                    aria-label={`Use ${colorOption.label.toLowerCase()} for alternate name ${index + 1}`}
                                    title={colorOption.label}
                                  />
                                ))}
                              </AltNameCalloutColorRow>
                            </AltNameCalloutColorSection>
                          </div>
                          );
                        })}
                      </div>
                      <BoardInfoHint>
                        Add up to four alternate labels for this pin. X and Y use SVG coordinates for the alternate callout box.
                      </BoardInfoHint>
                    </FormGroup>

                    <FormGroup style={{ gridColumn: '1 / -1' }}>
                      <Label>Alternate Functions</Label>
                      <TextArea
                        value={pin.alternate_functions || ''}
                        onChange={(e) => handlePinUpdate(pin.id, 'alternate_functions', e.target.value)}
                        placeholder="e.g., SPI MOSI, I2C SDA, PWM, ADC"
                      />
                    </FormGroup>

                    <FormGroup style={{ gridColumn: '1 / -1' }}>
                      <Label>Description</Label>
                      <RichTextEditor
                        value={pin.description || ''}
                        onChange={(value) => handlePinUpdate(pin.id, 'description', value)}
                        placeholder="Additional pin information with formatting..."
                        height={100}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>X Position</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={pinPositions[pin.id]?.x ?? pin.position_x ?? ''}
                        onChange={(e) => handlePinPositionChange(pin.id, 'x', e.target.value)}
                        placeholder="X coordinate"
                        title="SVG X coordinate for pin position"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Y Position</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={pinPositions[pin.id]?.y ?? pin.position_y ?? ''}
                        onChange={(e) => handlePinPositionChange(pin.id, 'y', e.target.value)}
                        placeholder="Y coordinate"
                        title="SVG Y coordinate for pin position"
                      />
                    </FormGroup>
                  </PinDetails>
                </PinItem>
              ))}
            </PinList>
        </PinEditor>
          )}
          </ContentGrid>

          <BulkPinMetadataPanel>
            <BulkPinMetadataHeader>
              <div>
                <BulkPinMetadataTitle>Board Details JSON</BulkPinMetadataTitle>
                <BulkPinMetadataSubtitle>
                  Paste a JSON object here to update board-level metadata immediately, or export the current board details as a JSON template you can edit and reapply.
                </BulkPinMetadataSubtitle>
              </div>
            </BulkPinMetadataHeader>

            <BulkPinMetadataTextArea
              value={boardMetadataJson}
              onChange={(event) => setBoardMetadataJson(event.target.value)}
              placeholder={BOARD_METADATA_JSON_EXAMPLE}
              spellCheck={false}
              disabled={saving || applyingBoardMetadataJson}
            />

            <BulkPinMetadataActions>
              <BoardInfoButton
                type="button"
                className="primary"
                onClick={handleApplyBoardMetadataJson}
                disabled={saving || applyingBoardMetadataJson || !(boardMetadataJson || '').trim()}
              >
                <Save size={16} />
                {applyingBoardMetadataJson ? 'Applying JSON...' : 'Apply JSON to Board'}
              </BoardInfoButton>
              <BoardInfoButton
                type="button"
                className="secondary"
                onClick={loadCurrentBoardMetadataJson}
                disabled={saving || applyingBoardMetadataJson || !board}
              >
                Load Current Data
              </BoardInfoButton>
              <BoardInfoButton
                type="button"
                className="secondary"
                onClick={handleCopyCurrentBoardMetadataJson}
                disabled={saving || applyingBoardMetadataJson || !board}
              >
                Copy Current JSON
              </BoardInfoButton>
              <BoardInfoButton
                type="button"
                className="secondary"
                onClick={() => {
                  setBoardMetadataJson(BOARD_METADATA_JSON_EXAMPLE);
                  setBoardMetadataImportSummary('');
                }}
                disabled={saving || applyingBoardMetadataJson}
              >
                Load Example
              </BoardInfoButton>
              <BoardInfoButton
                type="button"
                className="secondary"
                onClick={() => {
                  setBoardMetadataJson('');
                  setBoardMetadataImportSummary('');
                }}
                disabled={saving || applyingBoardMetadataJson || (!(boardMetadataJson || '').trim() && !boardMetadataImportSummary)}
              >
                Clear
              </BoardInfoButton>
            </BulkPinMetadataActions>

            <CodeHelperText>
              Supported fields include <code>manufacturer</code>, <code>package_type</code>, <code>cpu</code>,
              {' '}<code>supply_voltage_min_v</code>, <code>supply_voltage_max_v</code>, <code>logic_voltage_min_v</code>,
              {' '}<code>logic_voltage_max_v</code>, <code>current_draw_typ_ma</code>, <code>current_draw_max_ma</code>,
              {' '}<code>rail_3v3_max_ma</code>, <code>rail_5v_max_ma</code>, <code>clock_speed</code>, <code>flash</code>,
              {' '}<code>ram</code>, <code>rom</code>, <code>supported_protocols</code>, <code>documentation_url</code>,
              {' '}<code>datasheet_url</code>, <code>verification_status</code>, and <code>description</code>.
            </CodeHelperText>

            <BulkPinMetadataExample>{BOARD_METADATA_JSON_EXAMPLE}</BulkPinMetadataExample>

            {boardMetadataImportSummary && (
              <ValidationMessage>{boardMetadataImportSummary}</ValidationMessage>
            )}
          </BulkPinMetadataPanel>

          {!isCalloutEditMode && (
            <BulkPinMetadataPanel>
              <BulkPinMetadataHeader>
                <div>
                  <BulkPinMetadataTitle>Bulk Pin Metadata JSON</BulkPinMetadataTitle>
                  <BulkPinMetadataSubtitle>
                    Paste a JSON array of pin patches here to update the pins table immediately, or export the current pins as JSON and round-trip them back after editing.
                    Each entry should identify a pin by <code>id</code>, <code>pin_number</code>, <code>pin_name</code>, or <code>svg_id</code>, then include only the pin metadata fields you want to overwrite.
                  </BulkPinMetadataSubtitle>
                </div>
              </BulkPinMetadataHeader>

              <BulkPinMetadataTextArea
                value={pinMetadataJson}
                onChange={(event) => setPinMetadataJson(event.target.value)}
                placeholder={PIN_METADATA_JSON_EXAMPLE}
                spellCheck={false}
                disabled={saving || applyingPinMetadataJson}
              />

              <BulkPinMetadataActions>
                <BoardInfoButton
                  type="button"
                  className="primary"
                  onClick={handleApplyPinMetadataJson}
                  disabled={saving || applyingPinMetadataJson || !(pinMetadataJson || '').trim()}
                >
                  <Save size={16} />
                  {applyingPinMetadataJson ? 'Applying JSON...' : 'Apply JSON to Pins'}
                </BoardInfoButton>
                <BoardInfoButton
                  type="button"
                  className="secondary"
                  onClick={loadCurrentPinMetadataJson}
                  disabled={saving || applyingPinMetadataJson || pins.length === 0}
                >
                  Load Current Data
                </BoardInfoButton>
                <BoardInfoButton
                  type="button"
                  className="secondary"
                  onClick={handleCopyCurrentPinMetadataJson}
                  disabled={saving || applyingPinMetadataJson || pins.length === 0}
                >
                  Copy Current JSON
                </BoardInfoButton>
                <BoardInfoButton
                  type="button"
                  className="secondary"
                  onClick={() => {
                    setPinMetadataJson(PIN_METADATA_JSON_EXAMPLE);
                    setPinMetadataImportSummary('');
                  }}
                  disabled={saving || applyingPinMetadataJson}
                >
                  Load Example
                </BoardInfoButton>
                <BoardInfoButton
                  type="button"
                  className="secondary"
                  onClick={() => {
                    setPinMetadataJson('');
                    setPinMetadataImportSummary('');
                  }}
                  disabled={saving || applyingPinMetadataJson || (!(pinMetadataJson || '').trim() && !pinMetadataImportSummary)}
                >
                  Clear
                </BoardInfoButton>
              </BulkPinMetadataActions>

              <CodeHelperText>
                Supported patch fields include <code>group_name</code> or <code>pin_group_id</code>, <code>pin_role</code>,
                {' '}<code>functions</code> or <code>primary_function</code>, <code>capability_tags</code>, <code>is_5v_tolerant</code>,
                {' '}<code>use_with_caution</code>, <code>caution_reason</code>, <code>alternate_pin_names</code>,
                {' '}<code>alternate_functions</code> or <code>alternate_function</code>, <code>description</code>, <code>position_x</code>,
                {' '}<code>position_y</code>, and <code>svg_id</code>.
              </CodeHelperText>

              <BulkPinMetadataExample>{PIN_METADATA_JSON_EXAMPLE}</BulkPinMetadataExample>

              {pinMetadataImportSummary && (
                <ValidationMessage>{pinMetadataImportSummary}</ValidationMessage>
              )}
            </BulkPinMetadataPanel>
          )}
        </MainColumn>
      </EditorWorkspace>
    </EditBoardContainer>
  );
};

export default EditBoard;
